import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  BellIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  CodeBracketIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
  BeakerIcon,
  BellSlashIcon,
  UsersIcon,
  LockClosedIcon,
  ArrowTopRightOnSquareIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import {
  fetchTenants, fetchAlerts, fetchTestResults, updateAlertStatus,
  getIpPrevalence, getIpPrevalenceUsers, postSessionTrace, postTokenUse, refreshIpEnrichment,
  fetchDetectionRules, getAlertTicket, closeAlertTicket, resolveDirectoryNames, resolveDirectoryName,
} from '../api/client';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { TIMEZONE_OPTIONS, TZ_STORAGE_KEY, getDefaultTimezone, getTzOffset, resolveIana } from './TimelineView';
import Pagination from './Pagination';
import CopyButton from './CopyButton';
import IpPrevalenceUsers from './ui/IpPrevalenceUsers';
import DevicePosturePanel from './DevicePosturePanel';
import Badge from './ui/Badge';
import { severityTone, alertStatusTone } from './ui/badgeTones';
import { RefreshButton, RefreshingBanner, LoadingCard } from './ui/LoadingState';
import SourceEventRenderer from './sourceEvents/SourceEventRenderer';
import RawEventJsonPanel from './sourceEvents/RawEventJsonPanel';
import SourceEventsPanel from './sourceEvents/SourceEventsPanel';
import AggregationSummary from './sourceEvents/AggregationSummary';
import { parseAggregationFields } from './sourceEvents/aggregationHelpers';
import { parseInboxRuleParameters } from './sourceEvents/exchangeRuleHelpers';
import AlertFactsPanel from './sourceEvents/AlertFactsPanel';
import { parseAlertFacts, alertFactsUserCell } from './sourceEvents/alertFactsHelpers';
import { normalizeIsoUtc } from '../utils/timeFormat';
import BaselineIpListDrawer from './BaselineIpListDrawer';
import { parseSecurityAlert, topEvidenceHighlight, securityAlertIpEvidence } from './sourceEvents/securityEvidenceHelpers';
import MailboxPairAllowlistFromAlertModal from './alerts/MailboxPairAllowlistFromAlertModal';
import UserAppPairAllowlistFromAlertModal from './alerts/UserAppPairAllowlistFromAlertModal';
import GroupAppAllowlistFromAlertModal from './alerts/GroupAppAllowlistFromAlertModal';
import SuppressAlertTitleModal from './alerts/SuppressAlertTitleModal';
import SuppressAlertDescriptionModal from './alerts/SuppressAlertDescriptionModal';
import SuppressAlertActorModal from './alerts/SuppressAlertActorModal';
import BenignIpAllowlistFromAlertModal from './alerts/BenignIpAllowlistFromAlertModal';

// Defender `security_alert` pass-through rules all share this python_function_name;
// their alerts' `operation` field carries the Defender $.title, which the 1-click
// action appends to the rule's parameters_json.excluded_alert_titles (v1.101.0).
const DEFENDER_PASSTHROUGH_FN = 'rule_defender_endpoint_alert_passthrough';

import SessionTracePanel from './alerts/SessionTracePanel';
import TokenUsePanel from './alerts/TokenUsePanel';
import SigninOutcomeBanner from './alerts/SigninOutcomeBanner';
import LinkedAuditEventPanel from './alerts/LinkedAuditEventPanel';
import { effectiveSigninOutcome, isCompromised } from '../utils/signinOutcome';
import { collectTargetResourceGuids, ResolvedId } from '../utils/directoryNames';
import { resolveBaselineComparison } from '../utils/baselineComparison';
import BaselineComparisonPanel from './alerts/BaselineComparisonPanel';
import UnfamiliarPropertiesPanel from './alerts/UnfamiliarPropertiesPanel';
import { useResizableCols } from './coverage/coverageShared';
import { isDemoMode } from '../utils/demoMode';

// Demo Mode hides the controls that WRITE (IP-enrichment refresh spends
// ProxyCheck quota + MERGEs the shared Delta table). Read-shaped pivots
// (prevalence, Session Trace, Token Use) keep working via the restore layer.
const DEMO = isDemoMode();

// Default widths for the 8 data columns. Drag-to-resize handles persist new
// widths in-session (no localStorage); the 2 leading icon columns (chevron +
// raw-record button) are 32px each and not user-resizable.
const ALERT_COL_DEFAULTS = [
  140, // Alert Time
  140, // Event Time
  110, // Severity
  220, // Rule
  200, // User
  170, // IP / Country
  120, // Status
  100, // Actions
];
const ALERT_ICON_COL_W = 32;

const sentence = (s) => {
  if (!s) return '';
  const str = String(s).toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Exchange app-only auth events populate user_id with a PUID (S-1-...), not a UPN.
// For display only, substitute the resolved app identity. Raw user_id is preserved
// on the entity and on hover (title attr) for search, filter, and technical drill-down.
// For aggregation rules, prefer delegate_upn (the actual actor, resolved by the
// detection pipeline from AADSessionId) when present; fall back to mailbox_owner_upn
// with an "(owner)" suffix so it's clear the identity shown is the alert trigger.
const displayActor = (item) => {
  // A local group change names both sides in its facts: "<actor> added <member>".
  const factsCell = alertFactsUserCell(item?.alertFactsJson);
  if (factsCell) return factsCell.display;

  const uid = item?.userId;
  const agg = parseAggregationFields(item?.rawEventJson);

  // Aggregation rules with a pipeline-resolved delegate UPN — that's a real
  // human identity; surface it.
  if (agg?.delegate_upn) return agg.delegate_upn;

  // App-only access (PUID-shaped UserId on an exchange alert): the actor IS the
  // app. Render "App: {display_name}" using the row-level app columns the
  // detection pipeline emits. This precedes the mailbox_owner_upn fallback
  // because for app-only flows the owner is decorative — the actor is the app.
  // Handoff: 2026-04-24_App-Actor-Display-Portal-Handoff.md (re-affirmed
  // 2026-05-26_App-Actor-Display-Followup-Portal-Handoff.md).
  if (uid && uid.startsWith('S-1-') && item?.logType === 'exchange') {
    const appName = item.appDisplayName || item.applicationId;
    if (appName) return `App: ${appName}`;
  }

  // Aggregation rules without delegate UPN AND no app identity → fall back to
  // the owner UPN with "(owner)" suffix so it's clear the rendered identity
  // is the alert trigger, not the actor.
  if (agg) {
    const ownerUpn = agg.mailbox_owner_upn ||
      (Array.isArray(agg.set_MailboxOwnerUPN) && agg.set_MailboxOwnerUPN.length > 0
        ? agg.set_MailboxOwnerUPN[0] : null);
    if (ownerUpn) return `${ownerUpn} (owner)`;
  }

  if (!uid) return '';
  // PUID outside of exchange (rare; no observed cases as of 2026-05-26 but
  // defensive) — still render "App: name" when we have a sibling name.
  if (uid.startsWith('S-1-')) {
    const name = item.appDisplayName || item.applicationId;
    return name ? `App: ${name}` : uid;
  }
  return uid;
};

// The identity to place on the clipboard for the User column copy button.
// Mirrors displayActor()'s branch order so the copied value always tracks the
// value shown, but WITHOUT the UI decoration ("(owner)" suffix / "App: "
// prefix) — a clean UPN/identity pastes cleanly into search/case fields.
// This diverges from the raw item.userId on aggregation alerts whose delegate
// is unresolved (display shows the mailbox owner, userId is still the raw
// actor), and on resolved-delegate/app-only alerts where userId is a PUID.
const copyActor = (item) => {
  const factsCell = alertFactsUserCell(item?.alertFactsJson);
  if (factsCell) return factsCell.copy;

  const uid = item?.userId;
  const agg = parseAggregationFields(item?.rawEventJson);

  if (agg?.delegate_upn) return agg.delegate_upn;

  if (uid && uid.startsWith('S-1-') && item?.logType === 'exchange') {
    const appName = item.appDisplayName || item.applicationId;
    if (appName) return appName;
  }

  if (agg) {
    const ownerUpn = agg.mailbox_owner_upn ||
      (Array.isArray(agg.set_MailboxOwnerUPN) && agg.set_MailboxOwnerUPN.length > 0
        ? agg.set_MailboxOwnerUPN[0] : null);
    if (ownerUpn) return ownerUpn;
  }

  if (!uid) return '';
  if (uid.startsWith('S-1-')) {
    return item.appDisplayName || item.applicationId || uid;
  }
  return uid;
};

// Hover text for the User column: the facts headline when it replaced the identity,
// otherwise the raw user_id (search / filter / drill-down value).
const actorTitle = (item) => alertFactsUserCell(item?.alertFactsJson)?.title || item?.userId || '';

// A change made ON a device (Defender Advanced Hunting / device events) has no Entra
// sign-in session, so the session and token pivots have nothing to pivot on.
const isDeviceEvent = (item) => item?.logType === 'adv_hunting' || item?.logType === 'device_security';

// Low-signal modifiedProperties suppressed from the Target Resource panel.
// Role.WellKnownObjectName is still read internally to decorate Role.DisplayName.
// ConsentAction.Permissions is a packed blob parsed separately into the Permissions Granted panel.
const HIDDEN_MODIFIED_PROPS = new Set([
  'Role.ObjectID',
  'Role.TemplateId',
  'Role.WellKnownObjectName',
  'TargetId.ServicePrincipalNames',
  'ConsentAction.Permissions',
]);

// ── JSON diff utilities for Modified Properties (e.g., ConditionalAccessPolicy) ─

// Metadata paths that always change on any update — filtered from diffs.
const DIFF_SKIP_ROOTS = new Set(['id', 'createdDateTime', 'modifiedDateTime', 'displayName']);

// Parse through up to 3 layers of JSON string encoding to reach a JS object.
function tryParseAsObject(v) {
  if (!v) return null;
  let val = v;
  for (let i = 0; i < 3 && typeof val === 'string'; i++) {
    try { val = JSON.parse(val); } catch { return null; }
  }
  return (val && typeof val === 'object' && !Array.isArray(val)) ? val : null;
}

// Flatten { a: { b: 1 } } → { 'a.b': 1 }. Arrays kept as leaf values.
function flattenObj(obj, prefix = '') {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenObj(value, path));
    } else {
      result[path] = value;
    }
  }
  return result;
}

function serVal(v) {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

// Compute { changed, added, removed } between two objects.
function computeJsonDiff(oldObj, newObj) {
  const oldFlat = flattenObj(oldObj);
  const newFlat = flattenObj(newObj);
  const allPaths = new Set([...Object.keys(oldFlat), ...Object.keys(newFlat)]);
  const changed = [], added = [], removed = [];

  for (const path of allPaths) {
    if (DIFF_SKIP_ROOTS.has(path.split('.')[0])) continue;
    const inOld = path in oldFlat, inNew = path in newFlat;
    if (inOld && inNew) {
      if (serVal(oldFlat[path]) !== serVal(newFlat[path])) {
        if (Array.isArray(oldFlat[path]) && Array.isArray(newFlat[path])) {
          const os = new Set(oldFlat[path].map(serVal));
          const ns = new Set(newFlat[path].map(serVal));
          const ai = newFlat[path].filter(x => !os.has(serVal(x)));
          const ri = oldFlat[path].filter(x => !ns.has(serVal(x)));
          if (ai.length || ri.length) changed.push({ path, type: 'array', addedItems: ai, removedItems: ri });
        } else {
          changed.push({ path, type: 'value', oldVal: oldFlat[path], newVal: newFlat[path] });
        }
      }
    } else if (inNew) {
      added.push({ path, value: newFlat[path] });
    } else {
      removed.push({ path, value: oldFlat[path] });
    }
  }
  return { changed, added, removed };
}

// True when a modifiedProperty side carries no real value — covers actual null,
// empty string, and the literal "null" string Microsoft writes on create/delete.
function isNullishVal(v) {
  if (v === null || v === undefined || v === '') return true;
  if (typeof v === 'string') { const t = v.trim(); return t === '' || t === 'null'; }
  return false;
}

// Format a diff value for display — truncate long strings.
function diffVal(v) {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  if (typeof v === 'string') return v.length > 80 ? v.slice(0, 77) + '…' : v;
  if (Array.isArray(v)) {
    if (v.length === 0) return '[]';
    const items = v.map(x => typeof x === 'string' ? x : JSON.stringify(x));
    const joined = items.join(', ');
    return joined.length > 120 ? `[${v.length} items]` : `[${joined}]`;
  }
  return JSON.stringify(v);
}

// Walk "[[entry], [entry], ...]" and return each inner entry as a parsed k→v object.
// Entry format (Microsoft audit formatter, not JSON):
//   "Id: ..., ClientId: ..., PrincipalId: , ResourceId: <guid>, ConsentType: AllPrincipals,
//    Scope: <space-separated scopes>, CreatedDateTime: , LastModifiedDateTime"
function parseConsentEntryList(listText) {
  if (!listText) return [];
  let depth = 0;
  let start = -1;
  const rawEntries = [];
  for (let i = 0; i < listText.length; i++) {
    const ch = listText[i];
    if (ch === '[') {
      depth++;
      if (depth === 2) start = i + 1;
    } else if (ch === ']') {
      if (depth === 2 && start >= 0) {
        rawEntries.push(listText.slice(start, i));
        start = -1;
      }
      depth--;
    }
  }
  return rawEntries.map(entry => {
    const obj = {};
    // Key names (Id, ClientId, PrincipalId, ResourceId, ConsentType, Scope,
    // CreatedDateTime, LastModifiedDateTime) don't contain commas, but Scope
    // values can contain spaces. Split only on ", Key: " boundaries.
    const pairs = entry.split(/,\s+(?=[A-Za-z][A-Za-z0-9]*:\s)/);
    for (const p of pairs) {
      const m = p.match(/^\s*([A-Za-z][A-Za-z0-9]*):\s*(.*?)\s*$/);
      if (m) obj[m[1]] = m[2];
    }
    return obj;
  });
}

// Parse the two shapes Microsoft uses for consent-to-app events. Each returned
// permission is tagged with a status — 'new' (granted by this consent call),
// 'existing' (already granted before; re-confirmed), or 'revoked' (granted
// before but removed by this call).
//
// Shape A: parent "Consent to application" + N correlated child events grouped
//   into _correlated_events[] by the pipeline. Each child event is an explicit
//   "Add app role assignment" / "Add delegated permission grant" — so every
//   permission surfaced from Shape A is by definition 'new'.
// Shape B: parent event only. All granted scopes packed into one string in
//   modifiedProperties["ConsentAction.Permissions"] as "[OLD_LIST] => [NEW_LIST]".
//   Diff by (ResourceId, scope) key to classify each entry as new / existing /
//   revoked. (ConsentAction.Permissions only tracks delegated scopes — app role
//   assignments always flow through Shape A.)
function extractConsentPermissions(rawEvent) {
  if (!rawEvent) return [];
  const strip = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v !== 'string') return String(v);
    try { const parsed = JSON.parse(v); return typeof parsed === 'string' ? parsed : v; }
    catch { return v; }
  };

  // ── Shape A: correlated child events ─────────────────────────────────────
  const correlated = rawEvent._correlated_events;
  if (Array.isArray(correlated) && correlated.length > 0) {
    const perms = [];
    for (const evt of correlated) {
      const activity = evt?.activityDisplayName;
      const targets = evt?.targetResources;
      if (!Array.isArray(targets) || targets.length === 0) continue;
      const apiTarget = targets.find(t => Array.isArray(t.modifiedProperties) && t.modifiedProperties.length > 0) || targets[0];
      const apiName = apiTarget?.displayName || '';
      const props = apiTarget?.modifiedProperties || [];
      const getProp = (name) => {
        const p = props.find(x => x.displayName === name);
        return p ? strip(p.newValue) : '';
      };
      if (activity === 'Add app role assignment to service principal') {
        const scope = getProp('AppRole.Value');
        const description = getProp('AppRole.DisplayName');
        if (scope) perms.push({ kind: 'Application', api: apiName, scope, description, status: 'new' });
      } else if (activity === 'Add delegated permission grant') {
        const scope = getProp('DelegatedPermissionGrant.Scope');
        const consentType = getProp('DelegatedPermissionGrant.ConsentType');
        if (scope) {
          for (const s of scope.split(/\s+/).filter(Boolean)) {
            perms.push({ kind: 'Delegated', api: apiName, scope: s, consentType, status: 'new' });
          }
        }
      }
    }
    if (perms.length > 0) return perms;
  }

  // ── Shape B: ConsentAction.Permissions blob on the parent event ──────────
  const targets = rawEvent.targetResources;
  if (!Array.isArray(targets)) return [];
  const perms = [];
  for (const t of targets) {
    const mp = (t?.modifiedProperties || []).find(p => p.displayName === 'ConsentAction.Permissions');
    if (!mp) continue;
    const newText = strip(mp.newValue);
    const oldText = strip(mp.oldValue);
    if (!newText && !oldText) continue;

    // The "=> " separates old list from new list when both are present on the same value.
    const splitList = (text) => {
      if (!text) return '';
      const arrowIdx = text.lastIndexOf(' => ');
      if (arrowIdx < 0) return text;
      return { old: text.slice(0, arrowIdx), new: text.slice(arrowIdx + 4) };
    };

    // Microsoft sometimes stores "[OLD] => [NEW]" in newValue and leaves oldValue null
    // (observed in both example records), and sometimes splits them across oldValue/newValue.
    // Handle both.
    let oldPart = '';
    let newPart = '';
    const splitFromNew = splitList(newText);
    if (typeof splitFromNew === 'object') {
      oldPart = splitFromNew.old;
      newPart = splitFromNew.new;
    } else {
      newPart = splitFromNew;
    }
    if (!oldPart && oldText) {
      const splitFromOld = splitList(oldText);
      oldPart = typeof splitFromOld === 'object' ? splitFromOld.new : splitFromOld;
    }

    const oldEntries = parseConsentEntryList(oldPart);
    const newEntries = parseConsentEntryList(newPart);

    // Index old scopes by (ResourceId, scope) for diff lookup.
    const oldKey = (resourceId, scope) => `${resourceId}|${scope}`;
    const oldSet = new Set();
    for (const e of oldEntries) {
      const resourceId = e.ResourceId || '';
      for (const s of (e.Scope || '').split(/\s+/).filter(Boolean)) {
        oldSet.add(oldKey(resourceId, s));
      }
    }

    // Emit NEW + EXISTING from the new list, then REVOKED from the old-only diff.
    const newKeys = new Set();
    for (const e of newEntries) {
      const resourceId = e.ResourceId || '';
      const consentType = e.ConsentType || '';
      for (const s of (e.Scope || '').split(/\s+/).filter(Boolean)) {
        const k = oldKey(resourceId, s);
        newKeys.add(k);
        perms.push({
          kind: 'Delegated',
          api: '',
          scope: s,
          consentType,
          status: oldSet.has(k) ? 'existing' : 'new',
        });
      }
    }
    for (const e of oldEntries) {
      const resourceId = e.ResourceId || '';
      const consentType = e.ConsentType || '';
      for (const s of (e.Scope || '').split(/\s+/).filter(Boolean)) {
        const k = oldKey(resourceId, s);
        if (!newKeys.has(k)) {
          perms.push({ kind: 'Delegated', api: '', scope: s, consentType, status: 'revoked' });
        }
      }
    }
  }
  return perms;
}

// ── Azure Authorization scope parsing ───────────────────────────────────────

// Parses an Azure resourceId like
//   /subscriptions/{sub}/resourceGroups/{rg}/providers/Microsoft.KeyVault/vaults/{name}
//     /providers/Microsoft.Authorization/roleAssignments/{guid}
// and returns the scope the authorization artifact was applied to (the Key Vault),
// plus the artifact type + id (roleAssignments / {guid}).
// ── Severity helpers ────────────────────────────────────────────────────────

function severityIcon(severity) {
  switch (severity) {
    case 'critical':
    case 'high':   return <ShieldExclamationIcon className="h-3.5 w-3.5" />;
    case 'medium': return <ExclamationTriangleIcon className="h-3.5 w-3.5" />;
    default:       return <InformationCircleIcon className="h-3.5 w-3.5" />;
  }
}

function statusLabel(status) {
  switch (status) {
    case 'new':          return 'New';
    case 'acknowledged': return 'Acknowledged';
    case 'closed':       return 'Closed';
    case 'test':         return 'Test';
    default:             return status;
  }
}

// ── IP / port helpers ────────────────────────────────────────────────────────

/**
 * Returns just the IP address with port and IPv6 brackets stripped.
 * Examples:
 *   "1.2.3.4:5678"                              → "1.2.3.4"
 *   "[2601:46:...:5f39]:56294"                  → "2601:46:...:5f39"
 *   "[2601:46:...:5f39]"                        → "2601:46:...:5f39"
 *   "2601:46:...:5f39"                          → "2601:46:...:5f39"
 */
function stripPort(ipPort) {
  if (!ipPort) return '';
  let ip = ipPort;
  // IPv6 in [addr]:port notation — take the bracketed portion
  if (ip.startsWith('[')) {
    const close = ip.indexOf(']');
    if (close !== -1) {
      return ip.slice(1, close);
    }
  }
  // IPv4 "addr:port" — strip trailing numeric port if last colon looks like a port
  const colon = ip.lastIndexOf(':');
  if (colon !== -1) {
    const maybePart = ip.slice(colon + 1);
    if (/^\d+$/.test(maybePart) && ip.indexOf(':') === colon) {
      // Only one colon → definitely IPv4:port
      return ip.slice(0, colon);
    }
  }
  return ip;
}

// ── Collapsible wrapper for structured (non-JSON) content ───────────────────
// Chevron + uppercase tracking-wide title; matches the visual idiom of the
// shared RawEventJsonPanel (which is the JSON-payload equivalent).

function CollapsibleSection({ title, titleClass = 'text-gray-400', defaultCollapsed = true, children }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div>
      <div
        className="flex items-center gap-1.5 cursor-pointer select-none"
        onClick={(e) => { e.stopPropagation(); setCollapsed(c => !c); }}
      >
        {collapsed
          ? <ChevronRightIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
          : <ChevronDownIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />}
        <span className={`text-xs ${titleClass} font-semibold uppercase tracking-wide`}>{title}</span>
      </div>
      {!collapsed && <div className="mt-2">{children}</div>}
    </div>
  );
}

// ── IP risk level color ─────────────────────────────────────────────────────

function riskLevelColor(level) {
  if (level == null) return 'text-gray-500';
  if (level >= 80) return 'text-red-400';
  if (level >= 50) return 'text-orange-400';
  if (level >= 30) return 'text-yellow-400';
  return 'text-green-400';
}

// ── Date helpers ─────────────────────────────────────────────────────────────

/** Today's local date as YYYY-MM-DD (valid value for <input type="date">). */
function todayYmd() {
  return new Date().toLocaleDateString('en-CA');
}

/** N days before today, local, as YYYY-MM-DD. */
function daysAgoYmd(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString('en-CA');
}

/**
 * Parse an alert timestamp to epoch ms. `normalizeIsoUtc` handles the no-zone case
 * (Databricks writes `event_time` without a trailing `Z`) and truncates >3-digit
 * fractional seconds so the parse is deterministic across browsers (mirrors `formatTs`).
 * Returns null when the value is missing/unparseable.
 */
function parseAlertTs(ts) {
  if (!ts) return null;
  const ms = Date.parse(normalizeIsoUtc(ts));
  return Number.isFinite(ms) ? ms : null;
}

// ── Format timestamp ────────────────────────────────────────────────────────

function formatTs(ts, iana) {
  if (!ts) return '—';
  try {
    // normalizeIsoUtc: append 'Z' when no zone is present (Databricks writes UTC)
    // and truncate sub-millisecond precision (".862744" -> ".862") — Firefox/Safari
    // reject >3 fractional digits as Invalid Date, which would drop us into the
    // catch below and render the raw, un-converted UTC string.
    // 'Local' carries an empty iana — resolveIana maps it to the browser zone; an
    // empty timeZone option throws in toLocaleString and would also fall back to raw.
    return new Date(normalizeIsoUtc(ts)).toLocaleString('en-US', {
      timeZone: resolveIana(iana),
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch {
    return ts;
  }
}

// ── Auth App Policy Evaluation — humanize per Microsoft Graph beta enum ──
// Reference: https://learn.microsoft.com/en-us/graph/api/resources/authenticationapppolicydetails
//   authenticationEvaluation enum: success | failure | unknownFutureValue
//   adminConfiguration  enum: notApplicable | enabled | disabled | unknownFutureValue
//   status              enum: unknown, appLockOutOfDate, appLockEnabled, appLockDisabled,
//                              appContextOutOfDate, appContextShown, appContextNotShown,
//                              locationContextOutOfDate, locationContextShown, locationContextNotShown,
//                              numberMatchOutOfDate, numberMatchCorrectNumberEntered,
//                              numberMatchIncorrectNumberEntered, numberMatchDeny,
//                              tamperResistantHardwareOutOfDate, tamperResistantHardwareUsed,
//                              tamperResistantHardwareNotUsed, unknownFutureValue
//   policyName          String (free-form; Microsoft writes things like "Number matching")

const EVAL_LABELS = {
  success: 'success',
  failure: 'failure',
  unknownFutureValue: 'unknown',
};

const STATUS_LABELS = {
  unknown: 'unknown',
  appLockOutOfDate: 'app lock out of date',
  appLockEnabled: 'app lock enabled',
  appLockDisabled: 'app lock disabled',
  appContextOutOfDate: 'app context out of date',
  appContextShown: 'app context shown',
  appContextNotShown: 'app context not shown',
  locationContextOutOfDate: 'location context out of date',
  locationContextShown: 'location context shown',
  locationContextNotShown: 'location context not shown',
  numberMatchOutOfDate: 'number match out of date',
  numberMatchCorrectNumberEntered: 'correct number entered',
  numberMatchIncorrectNumberEntered: 'incorrect number entered',
  numberMatchDeny: 'denied',
  tamperResistantHardwareOutOfDate: 'tamper-resistant hardware out of date',
  tamperResistantHardwareUsed: 'tamper-resistant hardware used',
  tamperResistantHardwareNotUsed: 'tamper-resistant hardware not used',
  unknownFutureValue: 'unknown',
};

function camelToSentence(s) {
  if (!s) return '';
  const spaced = s.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function humanizePolicyName(name) {
  if (!name) return 'Policy';
  // Microsoft sometimes writes friendly already (e.g. "Number matching") — pass through.
  // If we got camelCase or PascalCase, convert.
  if (/[A-Z]/.test(name) && /[a-z]/.test(name) && !name.includes(' ')) {
    return camelToSentence(name);
  }
  return name;
}

function humanizePolicyEval(name, evaluation) {
  const policy = humanizePolicyName(name);
  const evalLabel = EVAL_LABELS[evaluation] || (evaluation || 'unknown').toLowerCase();
  return `${policy} ${evalLabel}`;
}

function humanizePolicyStatus(status) {
  if (!status) return null;
  return STATUS_LABELS[status] || camelToSentence(status).toLowerCase();
}

function evalTextColor(evaluation) {
  // Deliberately do NOT colour success green — a "success" here means the auth
  // app policy passed, which can equally describe a legitimate sign-in OR an
  // attacker successfully clearing MFA. Only failure is unambiguously informative.
  const v = (evaluation || '').toLowerCase();
  if (v === 'failure') return 'text-red-300';
  return 'text-gray-300';
}

// Defang a URL by replacing the dot between the registrable domain and the TLD
// (the last dot in the hostname) with [.]. Keeps the URL recognisable while
// breaking auto-link detection so the investigator cannot accidentally click it.
function defangUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url);
    const host = u.hostname;
    const lastDot = host.lastIndexOf('.');
    if (lastDot < 0) return url;
    const defangedHost = host.slice(0, lastDot) + '[.]' + host.slice(lastDot + 1);
    return url.replace(host, defangedHost);
  } catch {
    // Fallback: locate the host by stripping the scheme + path manually
    const protoEnd = url.indexOf('://');
    const start = protoEnd >= 0 ? protoEnd + 3 : 0;
    const slash = url.indexOf('/', start);
    const hostEnd = slash > 0 ? slash : url.length;
    const host = url.slice(start, hostEnd);
    const lastDot = host.lastIndexOf('.');
    if (lastDot < 0) return url;
    return url.slice(0, start + lastDot) + '[.]' + url.slice(start + lastDot + 1);
  }
}

// ── Expanded row detail ─────────────────────────────────────────────────────

function DetailField({ label, children, mono = false }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-500 shrink-0 w-24">{label}</span>
      <span className={`text-gray-300 break-all ${mono ? 'font-mono' : ''}`}>{children || '—'}</span>
    </div>
  );
}

// Human-readable PSA system label.
function psaSystemLabel(sys) {
  if (sys === 'autotask') return 'AutoTask';
  if (sys === 'connectwise') return 'ConnectWise';
  return sys || 'PSA';
}

function AlertDetail({ alert, tzIana, onSigninOutcomeResolved, onLinkedAuditResolved, onDirNameResolved }) {
  const rawEvent = useMemo(() => {
    if (!alert.rawEventJson || alert.rawEventJson.startsWith('blob:')) return null;
    try { return JSON.parse(alert.rawEventJson); } catch { return null; }
  }, [alert.rawEventJson]);

  const hasBlobEvents = !!(alert.sourceEventsBlobPath && alert.sourceEventsBlobPath.startsWith('blob:'));
  // When raw_event_json is an aggregation summary, the standard single-event
  // Source Event panel renders mostly empty (snake_case keys don't match) — the
  // dedicated AggregationSummary already shows the relevant fields, so skip it.
  const isAggregationEvent = !!parseAggregationFields(alert.rawEventJson);

  const deviation = useMemo(() => {
    if (!alert.baselineDeviation) return null;
    try { return JSON.parse(alert.baselineDeviation); } catch { return null; }
  }, [alert.baselineDeviation]);

  const baselineContext = useMemo(() => {
    if (!alert.baselineContextJson || alert.baselineContextJson === '' || alert.baselineContextJson.startsWith('blob:')) return null;
    try { return JSON.parse(alert.baselineContextJson); } catch { return null; }
  }, [alert.baselineContextJson]);

  // Consent-to-app permissions (if any). When non-empty, we render them in a
  // dedicated Permissions Granted panel and suppress the Modified Properties
  // sub-section on Target Resource (those props are redundant ConsentContext metadata).
  const permissions = useMemo(() => extractConsentPermissions(rawEvent), [rawEvent]);
  const isConsentEvent = permissions.length > 0;

  const isSignInBlob = alert.correlatedSignInJson && alert.correlatedSignInJson.startsWith('blob:');

  const caPolicies = useMemo(() => {
    if (!rawEvent?.appliedConditionalAccessPolicies) return [];
    return Array.isArray(rawEvent.appliedConditionalAccessPolicies)
      ? rawEvent.appliedConditionalAccessPolicies : [];
  }, [rawEvent]);

  const [showNotApplied, setShowNotApplied] = useState(false);

  // ── Cross-tenant IP prevalence + Session Trace plumbing ───────────────
  // Both need an effective tenantId. Test-result rows (shown via the Show
  // Test Alerts toggle) sometimes ship envelopes whose tenantId field is
  // empty — we recover it from rawEventJson before bailing.
  // Session Trace also needs an AAD session ID, which lives at different
  // paths depending on log type:
  //   graph_signin → top-level rawEvent.sessionId
  //   Exchange     → rawEvent.AppAccessContext.AADSessionId
  //   non-signin alerts with a matched signin → correlatedSignInJson.sessionId
  const correlatedSigninParsed = useMemo(() => {
    const blob = alert.correlatedSignInJson || alert.correlatedSigninJson; // TestRunResults uses lowercase 'i'
    if (!blob || blob.startsWith('blob:')) return null;
    try { return JSON.parse(blob); } catch { return null; }
  }, [alert.correlatedSignInJson, alert.correlatedSigninJson]);

  // "IP in baseline" → click the count to open the full baseline-IP list drawer.
  const [ipDrawerOpen, setIpDrawerOpen] = useState(false);

  const effectiveTenantId = useMemo(() => {
    if (alert.tenantId) return alert.tenantId;
    if (rawEvent?.tenant_id)        return rawEvent.tenant_id;
    if (rawEvent?.tenantId)         return rawEvent.tenantId;
    if (rawEvent?.resourceTenantId) return rawEvent.resourceTenantId;
    if (rawEvent?.homeTenantId)     return rawEvent.homeTenantId;
    return null;
  }, [alert.tenantId, rawEvent]);

  const effectiveSessionId = useMemo(() => {
    if (alert.aadSessionId) return alert.aadSessionId;
    if (rawEvent?.sessionId) return String(rawEvent.sessionId);
    if (rawEvent?.AppAccessContext?.AADSessionId) return String(rawEvent.AppAccessContext.AADSessionId);
    if (correlatedSigninParsed?.sessionId) return String(correlatedSigninParsed.sessionId);
    if (correlatedSigninParsed?.AppAccessContext?.AADSessionId) return String(correlatedSigninParsed.AppAccessContext.AADSessionId);
    return null;
  }, [alert.aadSessionId, rawEvent, correlatedSigninParsed]);

  // Defender security_alert pass-through alerts (e.g. Entra ID Protection
  // password-spray) carry the IP + location inside the alert's evidence[]
  // (ipEvidence card); the pipeline never promotes them to the alert-row
  // client_ip / ip_country columns, so IP Intelligence reads blank. Lift them
  // out of the evidence so the panel renders + can be enriched on demand.
  const securityAlertIp = useMemo(() => {
    if (alert.logType !== 'security_alert' || !rawEvent) return null;
    return securityAlertIpEvidence(parseSecurityAlert(rawEvent));
  }, [alert.logType, rawEvent]);

  // Effective IP for the IP Intelligence panel + cross-tenant prevalence: the
  // alert-row client_ip when present, else the security_alert evidence IP.
  // Already port-stripped (the evidence IP carries no port).
  const effectiveIp = useMemo(
    () => stripPort(alert.clientIp) || securityAlertIp?.ip || null,
    [alert.clientIp, securityAlertIp]
  );

  // On-demand enrichment refresh state. When non-null, `enrichOverride`
  // overlays the alert's IP enrichment fields so the IP Intelligence + Baseline
  // Comparison panels re-render with the freshly-fetched values without a
  // page reload (the underlying alert row in storage is not rewritten — the
  // overlay lives until the row is collapsed/the page is refreshed).
  const [enrichOverride, setEnrichOverride] = useState(null);
  const [refreshingIp, setRefreshingIp]     = useState(null);
  const [refreshError, setRefreshError]     = useState(null);

  // Lazy-fetch cross-tenant IP prevalence on row expand. Single Databricks
  // count over the alert IP's last 30 days; drives the IP Intelligence
  // panel's shared-VPN-vs-isolated pill.
  const [ipPrevalence, setIpPrevalence] = useState(null);
  const [ipPrevErr, setIpPrevErr]       = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!effectiveTenantId || !effectiveIp) return;
    getIpPrevalence(alert.alertId, effectiveTenantId, effectiveIp)
      .then(d => { if (!cancelled) setIpPrevalence(d); })
      .catch(e => { if (!cancelled) setIpPrevErr(e.message || String(e)); });
    return () => { cancelled = true; };
  }, [alert.alertId, effectiveTenantId, effectiveIp]);

  // Lazy-resolve directory object GUIDs -> friendly names on row expand. For
  // directory_audit alerts (esp. Conditional Access policy edits), the
  // modifiedProperties reference groups / users / directory roles by bare GUID;
  // resolving them lets the Target Resource panel render the "Added"/"Removed"
  // group/user/role name next to the GUID. Display-only; re-fetches on re-expand.
  // Names come from three sources, merged into one map keyed by lowercased GUID:
  //   1. persisted on the alert row (resolvedDirectoryNamesJson) — analyst-resolved
  //      via the "Resolve Name" button in a prior session;
  //   2. auto-resolved on expand from the daily Entra snapshot (fast path);
  //   3. button-resolved this session (handleResolveName).
  const [dirNames, setDirNames] = useState(() => {
    if (!alert.resolvedDirectoryNamesJson) return {};
    try { return JSON.parse(alert.resolvedDirectoryNamesJson) || {}; } catch { return {}; }
  });
  useEffect(() => {
    let cancelled = false;
    if (!effectiveTenantId) return;
    const ids = collectTargetResourceGuids(rawEvent);
    if (ids.length === 0) return;
    resolveDirectoryNames(effectiveTenantId, ids)
      .then(d => { if (!cancelled && d?.resolved) setDirNames(prev => ({ ...d.resolved, ...prev })); })
      .catch(() => { /* display-only; ignore */ });
    return () => { cancelled = true; };
  }, [effectiveTenantId, rawEvent]);

  // Manual "Resolve Name" — for a GUID the daily snapshot doesn't have yet
  // (typically an object created the same day). Queries ATS (snapshot + 30-min
  // directory_audit mirror), persists onto the alert row, and lifts the result
  // so it survives collapse/re-expand + reloads.
  const [resolvingIds, setResolvingIds] = useState(() => new Set());
  const [notFoundIds, setNotFoundIds]   = useState(() => new Set());
  const handleResolveName = useCallback(async (id) => {
    if (!effectiveTenantId || !id) return;
    setResolvingIds(prev => { const n = new Set(prev); n.add(id); return n; });
    setNotFoundIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    try {
      const res = await resolveDirectoryName(alert.alertId, {
        tenantId:     effectiveTenantId,
        id,
        eventTimeUtc: alert.eventTime || rawEvent?.createdDateTime || rawEvent?.CreationTime,
      });
      if (res?.found && res.name) {
        const info = { name: res.name, type: res.type };
        setDirNames(prev => ({ ...prev, [id]: info }));
        onDirNameResolved?.(alert.alertId, id, info);
      } else {
        setNotFoundIds(prev => { const n = new Set(prev); n.add(id); return n; });
      }
    } catch {
      setNotFoundIds(prev => { const n = new Set(prev); n.add(id); return n; });
    } finally {
      setResolvingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  }, [effectiveTenantId, alert.alertId, alert.eventTime, rawEvent, onDirNameResolved]);

  // Props spread onto every ResolvedId in the Target Resource diff so an
  // unresolved GUID offers a "Resolve Name" button (real alerts only — test
  // rows can't persist, so the button is omitted there).
  const dirResolveProps = alert._isTestResult
    ? { }
    : { onResolve: handleResolveName, resolvingIds, notFoundIds };

  // PSA ticket linkage (read-only). The notification dispatcher records the
  // ticket it opened in DetectionDispatchLog; this surfaces system + ticket id.
  // Skip test-result rows (they never have a ticket) and only render when a
  // live ticket is linked.
  const [psaTicket, setPsaTicket] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (alert._isTestResult || !effectiveTenantId || !alert.alertId) return;
    getAlertTicket(alert.alertId, effectiveTenantId)
      .then(d => { if (!cancelled && d?.ticketLinked) setPsaTicket(d); })
      .catch(() => { /* display-only; ignore */ });
    return () => { cancelled = true; };
  }, [alert.alertId, alert._isTestResult, effectiveTenantId]);

  // On-demand IP user list (button-triggered, NOT auto-fetched). Lists the
  // same-tenant UPNs that share this IP over the last 30 days, plus a count of
  // users/tenants in other tenants (UPNs withheld for MSP separation).
  const [ipUsers, setIpUsers]               = useState(null);
  const [ipUsersLoading, setIpUsersLoading] = useState(false);
  const [ipUsersErr, setIpUsersErr]         = useState(null);
  const loadIpUsers = useCallback(async () => {
    if (!effectiveTenantId || !effectiveIp || ipUsersLoading) return;
    setIpUsersLoading(true); setIpUsersErr(null);
    try {
      const res = await getIpPrevalenceUsers(alert.alertId, effectiveTenantId, effectiveIp);
      setIpUsers(res);
    } catch (e) {
      setIpUsersErr(e.message || String(e));
    } finally {
      setIpUsersLoading(false);
    }
  }, [alert.alertId, effectiveTenantId, effectiveIp, ipUsersLoading]);

  // Session Trace (on-demand). bumped traceToken from AlertRow fires it.
  const [sessionTrace, setSessionTrace]     = useState(null);
  const [sessionTraceLoading, setStLoading] = useState(false);
  const [sessionTraceErr, setStErr]         = useState(null);
  const traceSession = useCallback(async () => {
    if (!effectiveSessionId || !effectiveTenantId || sessionTraceLoading) return;
    setStLoading(true); setStErr(null);
    try {
      const res = await postSessionTrace(alert.alertId, {
        tenantId:  effectiveTenantId,
        sessionId: effectiveSessionId,
        startUtc:  alert.eventTime || rawEvent?.createdDateTime || rawEvent?.CreationTime,
        userId:    alert.userId   || rawEvent?.UserId            || rawEvent?.userPrincipalName,
        hours:     24,
      });
      setSessionTrace(res);
    } catch (e) {
      setStErr(e.message || String(e));
    } finally {
      setStLoading(false);
    }
  }, [effectiveSessionId, effectiveTenantId, alert.alertId, alert.eventTime, alert.userId, rawEvent, sessionTraceLoading]);

  // Token Use (on-demand) — pivot into the non-interactive sign-in feed (AiTM token replay).
  const [tokenUse, setTokenUse]            = useState(null);
  const [tokenUseLoading, setTuLoading]    = useState(false);
  const [tokenUseErr, setTuErr]            = useState(null);
  const loadTokenUse = useCallback(async () => {
    const upn = alert.userId || rawEvent?.userPrincipalName || rawEvent?.UserId;
    if (!effectiveTenantId || !upn || tokenUseLoading) return;
    // The Entra object id is a sibling of the UPN and is where Teams / app token
    // traffic is recorded in the non-interactive feed; include it so the pivot does
    // not under-match. In a graph_signin raw event `$.userId` IS the object GUID.
    // Only pass a GUID-shaped value — a UPN would just dedup server-side, so this
    // never regresses when no object id is available.
    const GUID_RE = /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/i;
    const objCand = String(rawEvent?.userId || rawEvent?.userObjectId || '');
    const entraObjectId = GUID_RE.test(objCand) ? objCand : '';
    setTuLoading(true); setTuErr(null);
    try {
      const res = await postTokenUse(alert.alertId, {
        tenantId: effectiveTenantId,
        userId:   upn,
        entraObjectId,
        startUtc: alert.eventTime || rawEvent?.createdDateTime || rawEvent?.CreationTime,
        hours:    24,
      });
      setTokenUse(res);
    } catch (e) {
      setTuErr(e.message || String(e));
    } finally {
      setTuLoading(false);
    }
  }, [effectiveTenantId, alert.alertId, alert.eventTime, alert.userId, rawEvent, tokenUseLoading]);

  // On-demand IP enrichment refresh — synchronous call to /api/manage/ip-enrichment/refresh.
  // On 409 (enrichment was refreshed in the last 24h), confirm with the user and re-send
  // with force=true. On success, stash the response so the IP Intelligence + Baseline
  // Comparison panels overlay the fresh values immediately.
  const handleRefreshIpEnrichment = useCallback(async (ip, force = false) => {
    if (!ip) return;
    try {
      setRefreshingIp(ip);
      setRefreshError(null);
      // Persist back onto the alert row for real alerts so the fresh values
      // survive a reload. Test-result rows (DetectionTestResults) stay overlay-only.
      const persistArgs = (!alert._isTestResult && alert.alertId && effectiveTenantId)
        ? { alertId: alert.alertId, tenantId: effectiveTenantId }
        : {};
      const fresh = await refreshIpEnrichment(ip, { force, ...persistArgs });
      setEnrichOverride({
        ipRiskLevel:  fresh.ipRiskLevel ?? null,
        ipCountry:    fresh.ipCountry ?? null,
        ipProvider:   fresh.ipProvider ?? null,
        ipDetections: fresh.ipDetections ?? null,
        regionName:   fresh.regionName ?? null,
        city:         fresh.city ?? null,
      });
    } catch (e) {
      if (e?.status === 409 && !force) {
        const lastUpdated = e.body?.lastUpdated;
        const hours = lastUpdated
          ? Math.max(0, Math.round((Date.now() - new Date(lastUpdated).getTime()) / 3600000))
          : null;
        const msg = hours != null
          ? `IP enrichment was last refreshed ${hours}h ago. Re-run anyway?`
          : 'IP enrichment was refreshed in the last 24h. Re-run anyway?';
        if (window.confirm(msg)) {
          return handleRefreshIpEnrichment(ip, true);
        }
        return;
      }
      setRefreshError(e?.message || 'Refresh failed.');
    } finally {
      setRefreshingIp(null);
    }
  }, [alert._isTestResult, alert.alertId, effectiveTenantId]);

  // Parse inbox-rule / operation parameters from the raw event. Covers both the
  // flat `Parameters` schema (New-/Set-/Remove-InboxRule) and the
  // `OperationProperties` schema (UpdateInboxRules — user-edited rules in Outlook/
  // OWA, where the forward-to target lives in RuleActions). See exchangeRuleHelpers.
  const parameters = useMemo(() => parseInboxRuleParameters(rawEvent), [rawEvent]);
  // Pipeline-owned per-kind facts (ALERT-FACTS-CONTRACT.md). When present they
  // REPLACE the parameters table above — the contract's rule is that a consumer
  // does not re-derive a kind's detail from raw_event_json. Absent/unknown ⇒ null
  // ⇒ the parameters table renders exactly as before.
  const hasAlertFacts = useMemo(() => parseAlertFacts(alert.alertFactsJson) !== null, [alert.alertFactsJson]);

  const eventTime = rawEvent?.CreationTime || rawEvent?.EventTimeUTC || alert.eventTime;
  const deviceDetail = rawEvent?.deviceDetail || null;
  const hasDevice = alert.deviceName || alert.deviceOs || alert.deviceIsCompliant || deviceDetail;

  // AiTM URL-click correlation: written by rule_aitm_url_click_to_new_ip_signin into the raw event.
  // Surfaces the click URL, click timestamp, and click→signin gap so the technician can factor the
  // origin of the user's session into their assessment.
  const aitmCorrelation = rawEvent?._aitm_correlation || null;

  // Defender (security_alert) threat highlight — lift the headline evidence (file
  // for malware, URL+IP for C2, process+user for credential access, + the related
  // file to investigate) into an always-visible banner at the top of the row.
  const defenderHighlight = useMemo(() => {
    if (alert.logType !== 'security_alert' || !rawEvent) return null;
    return topEvidenceHighlight(parseSecurityAlert(rawEvent));
  }, [alert.logType, rawEvent]);

  // Rule-specific analyst guidance: the "Anomalous access to other users'
  // mailboxes" rule (rule-008) also fires on legitimate-looking delegate/admin
  // mailbox access, so the analyst must decide whether the access was
  // authorized. Match on the rule GUID with a rule-name fallback.
  const isAnomalousMailboxAccessRule =
    alert.ruleId === 'rule_1066145e-1984-4f06-aff2-49b07f0ff80d' ||
    /anomalous access to other users/i.test(alert.ruleName || '');

  return (
    <div className="bg-gray-950 border-t border-gray-800 text-xs">

      {/* ── Sign-in outcome (Defender/AADIP pass-through): the load-bearing
            "PASSWORD COMPROMISED" callout. Rendered FIRST so the credential
            verdict outranks everything else on the card. Re-check button shown
            on real alerts (not test rows). Renders nothing when the alert
            carries no `_signin_outcome` / no cloudLogonRequestEvidence. ── */}
      <SigninOutcomeBanner
        alert={alert}
        rawEvent={rawEvent}
        tzIana={tzIana}
        showRecheckButton={!alert._isTestResult}
        onResolved={onSigninOutcomeResolved}
      />

      {/* ── Source audit event link (Defender pass-through — e.g. "Mailbox
            Permission Change"): resolves the target mailbox / trustee / rights
            from the Exchange admin audit event. Button on real alerts only;
            renders nothing when the alert isn't a linkable pass-through. ── */}
      <LinkedAuditEventPanel
        alert={alert}
        rawEvent={rawEvent}
        tzIana={tzIana}
        showLinkButton={!alert._isTestResult}
        onResolved={onLinkedAuditResolved}
      />

      {/* ── Defender threat highlight (security_alert evidence headline) ── */}
      {defenderHighlight && (
        <div className={`px-6 py-3 border-b ${
          defenderHighlight.tone === 'critical' ? 'border-red-900/50 bg-red-950/20'
            : defenderHighlight.tone === 'warning' ? 'border-amber-900/50 bg-amber-950/20'
            : 'border-gray-800/60'
        }`}>
          <p className={`text-[10px] uppercase tracking-wide font-semibold mb-1 ${
            defenderHighlight.tone === 'critical' ? 'text-red-400'
              : defenderHighlight.tone === 'warning' ? 'text-amber-400'
              : 'text-gray-400'
          }`}>
            Threat Evidence{defenderHighlight.verdict && defenderHighlight.verdict !== 'unknown' ? ` · ${defenderHighlight.verdict}` : ''}
          </p>
          <p className="text-gray-100 break-words font-medium">
            {defenderHighlight.headline}
            {defenderHighlight.moreCount > 0 && (
              <span className="text-gray-500 font-normal"> · +{defenderHighlight.moreCount} more</span>
            )}
          </p>
        </div>
      )}

      {/* ── Close reason (set when an analyst closes/acknowledges an alert) ── */}
      {alert.closeReason && (
        <div className="px-6 py-3 border-b border-gray-800/60">
          <p className="text-amber-400 text-[10px] uppercase tracking-wide font-semibold mb-1">
            Resolution Note
            {alert.closeSource === 'psa' && (
              <span className="ml-2 inline-flex items-center rounded bg-sky-500/15 text-sky-300 px-1.5 py-0.5 text-[9px] font-medium normal-case tracking-normal">
                via PSA ticket{(alert.psaTicketNumber || alert.psaTicketId) ? ` #${alert.psaTicketNumber || alert.psaTicketId}` : ''}
              </span>
            )}
          </p>
          <p className="text-gray-200 whitespace-pre-wrap break-words">{alert.closeReason}</p>
          <p className="text-gray-500 text-[10px] mt-1">
            {alert.closedBy ? `Closed by ${alert.closedBy}` : 'Closed'}
            {alert.closedUtc ? ` · ${formatTs(alert.closedUtc, tzIana)}` : ''}
          </p>
        </div>
      )}

      {/* ── PSA Ticket (opened by the notification dispatcher) ── */}
      {psaTicket?.ticketLinked && (
        <div className="px-6 py-3 border-b border-gray-800/60">
          <p className="text-cyan-400 text-[10px] uppercase tracking-wide font-semibold mb-1">PSA Ticket</p>
          <div className="flex items-center flex-wrap gap-2">
            <Badge tone={psaTicket.ticketingSystem === 'autotask' ? 'purple' : 'info'}>
              {psaSystemLabel(psaTicket.ticketingSystem)}
            </Badge>
            <span className="group inline-flex items-center gap-1">
              {psaTicket.ticketUrl ? (
                <a
                  href={psaTicket.ticketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 hover:underline font-mono"
                  onClick={(e) => e.stopPropagation()}
                  title="Open ticket in the PSA (new tab)"
                >
                  #{psaTicket.ticketNumber || psaTicket.ticketId}
                </a>
              ) : (
                <span className="text-gray-200 font-mono">#{psaTicket.ticketNumber || psaTicket.ticketId}</span>
              )}
              <CopyButton text={psaTicket.ticketNumber || psaTicket.ticketId} />
            </span>
          </div>
          <p className="text-gray-500 text-[10px] mt-1">
            {psaTicket.ticketOpenedUtc ? `Opened ${formatTs(psaTicket.ticketOpenedUtc, tzIana)}` : 'Opened'}
          </p>
        </div>
      )}

      {/* ── Analyst guidance for the anomalous-mailbox-access rule (rule-008) ── */}
      {isAnomalousMailboxAccessRule && (
        <div className="px-6 py-3 border-b border-gray-800/60">
          <p className="text-sky-400 text-[10px] uppercase tracking-wide font-semibold mb-1">Evaluation Note</p>
          <p className="text-gray-200 whitespace-pre-wrap break-words">
            Evaluate event for the access being authorized or not. Rule was created to detect access being abused (like an admin accessing sensitive data without direct authorization).
          </p>
        </div>
      )}

      {/* ── AiTM Click Correlation (rule_aitm_url_click_to_new_ip_signin) ── */}
      {aitmCorrelation && (
        <div className="px-6 py-3 border-b border-gray-800/60">
          <p className="text-rose-400 text-[10px] uppercase tracking-wide font-semibold mb-2">AiTM Click Correlation</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-2">
            <div className="md:col-span-3">
              <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-0.5">Clicked URL</p>
              {aitmCorrelation.clicked_url ? (
                <span className="group inline-flex items-center gap-1 max-w-full">
                  {/* Defanged display: dot between registrable domain and TLD becomes [.] so the URL doesn't auto-link */}
                  <span className="font-mono text-rose-300 break-all" title={aitmCorrelation.clicked_url}>
                    {defangUrl(aitmCorrelation.clicked_url)}
                  </span>
                  <CopyButton text={aitmCorrelation.clicked_url} />
                </span>
              ) : (
                <span className="text-gray-600 italic">—</span>
              )}
            </div>
            <div>
              <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-0.5">Click Time</p>
              <p className="text-gray-300 font-mono">
                {aitmCorrelation.click_time ? formatTs(aitmCorrelation.click_time, tzIana) : '—'}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-0.5">Click → Sign-in Gap</p>
              <p className="text-gray-300 font-mono">
                {(typeof aitmCorrelation.click_to_signin_seconds === 'number')
                  ? (aitmCorrelation.click_to_signin_seconds < 60
                      ? `${aitmCorrelation.click_to_signin_seconds} seconds`
                      : `${Math.floor(aitmCorrelation.click_to_signin_seconds / 60)} minutes ${aitmCorrelation.click_to_signin_seconds % 60} seconds`)
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Aggregation Summary — when raw_event_json holds a detection-window
            payload (rather than a single source event), surface mailbox owner,
            window, folders, IPs, client-info up top. Renders nothing for
            standard single-event alerts. ── */}
      <AggregationSummary rawEventJson={alert.rawEventJson} tzIana={tzIana} />

      {/* ── Aggregation Source Events (blob) ── */}
      {hasBlobEvents && (
        <div className="px-6 py-3 border-b border-gray-800/60">
          <SourceEventsPanel blobPath={alert.sourceEventsBlobPath} tzIana={tzIana} rawEventJson={alert.rawEventJson} tenantId={alert.tenantId} alertId={alert.alertId || alert.rowKey} ruleName={alert.ruleName} source="alerts" />
        </div>
      )}

      {/* ── Source Event header (collapsible, collapsed by default) ── */}
      {rawEvent && !hasBlobEvents && !isAggregationEvent && (
        <div className="px-6 py-3 border-b border-gray-800/60">
          <CollapsibleSection title="Source Event (Microsoft data)">
            <SourceEventRenderer
              rawEvent={rawEvent}
              logType={alert.logType}
              tzIana={tzIana}
              fallbacks={{
                user: alert.userId,
                ip: alert.clientIp,
                app: alert.appDisplayName,
                operation: alert.operation,
                device: alert.deviceName,
              }}
            />
          </CollapsibleSection>
        </div>
      )}

      {/* ── IP Intelligence (collapsible, full-width, collapsed by default) ── */}
      {(effectiveIp || alert.ipRiskLevel != null || alert.ipCountry || alert.ipProvider || alert.ipDetections || enrichOverride) && (() => {
        // Overlay the freshly-refreshed enrichment on top of the alert row so
        // the panel re-renders in place after a successful refresh.
        const viewRiskLevel  = enrichOverride?.ipRiskLevel  ?? alert.ipRiskLevel;
        // Country falls back to the security_alert evidence (ipEvidence) for
        // Defender pass-through alerts whose alert row carries no ip_country.
        const viewCountry    = enrichOverride?.ipCountry    ?? alert.ipCountry ?? securityAlertIp?.country;
        const viewProvider   = enrichOverride?.ipProvider   ?? alert.ipProvider;
        const viewDetections = enrichOverride?.ipDetections ?? alert.ipDetections;
        // The IP enrichment was actually fetched (vs. the pipeline's default 0).
        // ProxyCheck-only `provider` is the reliable signal — country can come
        // from the Defender evidence without any enrichment having run.
        const hasRealEnrichment = !!(enrichOverride || alert.ipProvider);
        // Defender security_alert rows ship ip_risk_level=0 by default; showing a
        // green "0" reads as "low risk" on a password-spray IP. Until enriched,
        // show "—" (unknown) for those instead of the misleading 0.
        const displayRiskLevel = (alert.logType === 'security_alert' && !hasRealEnrichment)
          ? null : viewRiskLevel;
        // Enrich button lives here in IP Intelligence whenever an alert has an IP
        // — the most discoverable home for it. Available to every portal user.
        // The handler persists the fresh enrichment back onto the alert row
        // (survives reload). The Baseline Comparison panel keeps its own per-row
        // refresh affordances.
        // IP-enrichment refresh spends ProxyCheck quota + MERGEs the shared
        // Delta table (and may write the alert row), so it's hidden in Demo Mode.
        const showEnrichHere = !!effectiveIp && !DEMO;
        return (
        <div className="px-6 py-3 border-b border-gray-800/60">
          <CollapsibleSection title="IP Intelligence">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-1">
              <DetailField label="IP" mono>
                {effectiveIp ? (
                  <span className="group inline-flex items-center gap-0.5">
                    <a href={`https://www.iplocation.net/ip-lookup?query=${encodeURIComponent(effectiveIp)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-blue-400 hover:text-blue-300 hover:underline" onClick={(e) => e.stopPropagation()} title="Look up this IP on iplocation.net (opens a new tab)">{effectiveIp}<ArrowTopRightOnSquareIcon className="h-3 w-3 flex-none" /></a>
                    <CopyButton text={effectiveIp} />
                    {showEnrichHere && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRefreshIpEnrichment(effectiveIp); }}
                        disabled={refreshingIp === effectiveIp}
                        title={`Fetch IP enrichment from ProxyCheck.io for ${effectiveIp}`}
                        className="inline-flex items-center text-gray-500 hover:text-sky-300 disabled:opacity-50"
                      >
                        <ArrowPathIcon className={`h-3 w-3 ${refreshingIp === effectiveIp ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </span>
                ) : '—'}
              </DetailField>
              <DetailField label="Risk score">
                <span className={riskLevelColor(displayRiskLevel)}>
                  {displayRiskLevel != null ? displayRiskLevel : '—'}
                </span>
              </DetailField>
              <DetailField label="Country">{viewCountry || '—'}</DetailField>
              <DetailField label="Provider">{viewProvider || '—'}</DetailField>
              {viewDetections ? (
                <DetailField label="Detections">
                  <span className="text-orange-400">{viewDetections}</span>
                </DetailField>
              ) : <div />}
            </div>
            {showEnrichHere && refreshError && (
              <div className="mt-1.5 text-red-400 text-[11px]">{refreshError}</div>
            )}
            {/* Cross-tenant prevalence pill (lazy-fetched). Sky-300 when the
                IP is heavily shared within this tenant (likely corporate
                VPN); amber when isolated (one tenant, one user). */}
            {effectiveIp && (
              <div className="mt-2 text-[11px]">
                <div className="flex items-center justify-between gap-2">
                  {ipPrevErr ? (
                    <span className="text-red-400">Prevalence lookup failed: {ipPrevErr}</span>
                  ) : !ipPrevalence ? (
                    <span className="text-gray-500 italic">Loading cross-tenant prevalence…</span>
                  ) : (() => {
                    const p = ipPrevalence;
                    const sharedVpn = p.sameTenantUserCount >= 5;
                    const isolated  = p.tenantCount <= 1 && p.sameTenantUserCount <= 1;
                    const tone = sharedVpn ? 'text-sky-300 border-sky-500/30 bg-sky-950/30'
                              : isolated  ? 'text-amber-300 border-amber-500/30 bg-amber-950/30'
                                          : 'text-gray-300 border-gray-700 bg-gray-900/40';
                    const parts = [
                      `${p.tenantCount} tenant${p.tenantCount === 1 ? '' : 's'}`,
                      `${p.userCount} user${p.userCount === 1 ? '' : 's'}`,
                      `same-tenant ${p.sameTenantUserCount} user${p.sameTenantUserCount === 1 ? '' : 's'}`,
                    ];
                    if (p.firstSeenUtc) {
                      const days = Math.max(0, Math.floor((Date.now() - new Date(p.firstSeenUtc).getTime()) / 86400000));
                      parts.push(`first seen ${days}d ago`);
                    }
                    return (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${tone}`}>
                        Cross-tenant ({p.windowDays}d): {parts.join(' · ')}
                      </span>
                    );
                  })()}
                  {/* On-demand: list the UPNs sharing this IP within the tenant. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); loadIpUsers(); }}
                    disabled={ipUsersLoading}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-blue-500/40 bg-blue-950/30 text-blue-300 hover:bg-blue-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <UsersIcon className="w-3 h-3" />
                    {ipUsersLoading ? 'Loading…' : (ipUsers ? 'Refresh users' : 'List users')}
                  </button>
                </div>

                {/* Lazy-loaded user list: same-tenant UPNs, authorized cross-tenant
                    UPNs (server-scoped), and a withheld count for the rest.
                    🔴 ONE component, shared with the case IP Addresses tab (v1.179.0).
                    This renders an authorization-sensitive response, so a second copy is
                    a second place for the withholding and truncation disclosures to be
                    forgotten -- the parity discipline of CLAUDE.md rule #13. */}
                {ipUsersErr && (
                  <div className="mt-1 text-red-400">User lookup failed: {ipUsersErr}</div>
                )}
                {ipUsers && (
                  <IpPrevalenceUsers
                    data={ipUsers}
                    ip={effectiveIp}
                    formatTs={(ts) => formatTs(ts, tzIana)}
                    className="mt-1.5"
                  />
                )}
              </div>
            )}
          </CollapsibleSection>
        </div>
        );
      })()}

      {/* ── Unfamiliar Sign-in Properties — Entra ID Protection pass-through only; renders nothing
            when the alert carries no unfamiliar_properties_json. Shared with Test Run Results (rule #13). ── */}
      {alert.unfamiliarPropertiesJson && (
        <div className="px-6 py-3 border-b border-gray-800/60">
          <UnfamiliarPropertiesPanel json={alert.unfamiliarPropertiesJson} />
        </div>
      )}

      {/* ── Baseline Comparison (collapsible, full-width) — shared with Test Run Results (rule #13) ── */}
      {(() => {
        const comparison = resolveBaselineComparison({
          storedJson: alert.baselineComparisonJson,
          baselineContext,
          rawEvent,
          correlatedSignin: correlatedSigninParsed,
          alert,
          enrichOverride,
        });
        const eventIp = rawEvent?.ipAddress ?? (alert.clientIp ? stripPort(alert.clientIp) : null);
        // Sign-in based alerts lead with location/device baseline signal, so expand by default for them.
        const baselineDefaultOpen = alert.logType === 'signin' || alert.logType === 'graph_signin';
        return (
          <>
          <div className="px-6 py-3 border-b border-gray-800/60">
            <BaselineComparisonPanel
              comparison={comparison}
              defaultOpen={baselineDefaultOpen}
              eventIp={eventIp}
              canRefresh={!DEMO}
              refreshing={refreshingIp === eventIp}
              refreshError={refreshError}
              onRefresh={() => handleRefreshIpEnrichment(eventIp)}
              onOpenIpDrawer={(lists) => setIpDrawerOpen(lists)}
            />
          </div>
          {ipDrawerOpen && (
            <BaselineIpListDrawer
              onClose={() => setIpDrawerOpen(false)}
              eventIp={eventIp}
              windows={[
                { label: '7-day', ips: ipDrawerOpen['7d'] || [] },
                { label: '30-day', ips: ipDrawerOpen['30d'] || [] },
              ]}
            />
          )}
          </>
        );
      })()}

      {/* ── Session Trace (24h pivot on AAD Session ID, on-demand) ── */}
      {/* ── Token Use (non-interactive sign-in pivot — AiTM token replay) ── */}
      {/* Neither applies to a change made on a device: no sign-in session to pivot on. */}
      {!isDeviceEvent(alert) && (<>
      <SessionTracePanel
        alert={{ ...alert, aadSessionId: effectiveSessionId }}
        sessionTrace={sessionTrace}
        loading={sessionTraceLoading}
        error={sessionTraceErr}
        onRun={traceSession}
        tzIana={tzIana}
      />

      <TokenUsePanel
        alert={alert}
        tokenUse={tokenUse}
        loading={tokenUseLoading}
        error={tokenUseErr}
        onRun={loadTokenUse}
        tzIana={tzIana}
      />
      </>)}

      {/* ── Alert facts (pipeline-defined per-kind detail; replaces the parameters table) ── */}
      {hasAlertFacts && (
        <AlertFactsPanel
          alertFactsJson={alert.alertFactsJson}
          className="px-6 py-3 border-b border-gray-800/60"
        />
      )}

      {/* ── Parameters table (Exchange inbox rules, Set- ops, etc.) — fallback when no facts ── */}
      {!hasAlertFacts && parameters && (
        <div className="px-6 py-3 border-b border-gray-800/60">
          <p className="font-semibold text-gray-400 uppercase tracking-wide text-xs mb-2">
            Rule / Operation Parameters
          </p>
          <table className="w-full max-w-2xl">
            <tbody>
              {parameters.map(({ Name, Value }) => (
                <tr key={Name} className="border-b border-gray-800/40 last:border-0">
                  <td className="py-1 pr-6 text-gray-400 w-56">{Name}</td>
                  <td className="py-1 text-gray-300 break-all">{Value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Bottom panels: Correlated Sign-in | Anomaly / IDs
            (Correlated Sign-in column is dropped entirely when there's no device/sign-in context,
             letting Anomaly/IDs occupy the full width instead of rendering a "No correlated sign-in" stub) ── */}
      {(() => {
        const showCorrelated = (hasDevice && !isSignInBlob) || isSignInBlob;
        return (
      <div className={`px-6 py-3 grid gap-6 ${showCorrelated ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {showCorrelated && (
          <div>
            <p className="font-semibold text-gray-400 mb-2 uppercase tracking-wide text-xs">Correlated Sign-in</p>
            {hasDevice && !isSignInBlob ? (
              <div className="space-y-1">
                <DetailField label="Device">
                  {(alert.deviceName || deviceDetail?.displayName)
                    ? (alert.deviceName || deviceDetail.displayName)
                    : <span className="text-gray-500 italic">Unknown</span>}
                </DetailField>
                {deviceDetail?.trustType && (
                  <DetailField label="Trust Type">{deviceDetail.trustType}</DetailField>
                )}
              </div>
            ) : (
              <p className="text-gray-600 italic">Stored externally (evidence blob)</p>
            )}
          </div>
        )}

        {/* Anomaly + IDs */}
        <div>
          <p className="font-semibold text-gray-400 mb-2 uppercase tracking-wide text-xs">
            Anomaly / IDs
          </p>
          {deviation && (
            <div className="space-y-1 mb-3">
              <DetailField label="Type">{deviation.type}</DetailField>
              {deviation.observed_ip && (
                <DetailField label="Observed IP" mono>
                  <span className="group inline-flex items-center gap-0.5">
                    <a href={`https://www.iplocation.net/ip-lookup?query=${encodeURIComponent(stripPort(deviation.observed_ip))}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline" onClick={(e) => e.stopPropagation()}>{stripPort(deviation.observed_ip)}</a>
                    <CopyButton text={stripPort(deviation.observed_ip)} />
                  </span>
                </DetailField>
              )}
              {deviation.observed_country && (
                <DetailField label="Country">{deviation.observed_country}</DetailField>
              )}
            </div>
          )}
          {(alert.skipIfEntraJoined || alert.skipIfCompliantDevice || alert.skipIfDrmmMatch || alert.skipIfIntuneManaged || alert.skipIfInTenantBaseline || alert.skipIfInUserBaseline || alert.skipIfCtapTrust || alert.skipIpv6PrefixMatch || alert.overrideIfMaliciousIp) && (
            <div className="mt-2 space-y-0.5">
              <p className="text-gray-500 text-[10px] uppercase tracking-wide font-semibold mb-1">Rule suppressions</p>
              {alert.skipIfEntraJoined && (
                <div className="flex items-center gap-1 text-[10px] text-blue-400">
                  <span>✓</span>
                  <span>Skip if Entra Joined</span>
                </div>
              )}
              {alert.skipIfIntuneManaged && (
                <div className="flex items-center gap-1 text-[10px] text-blue-400">
                  <span>✓</span>
                  <span>Skip if Intune Managed</span>
                </div>
              )}
              {alert.skipIfCompliantDevice && (
                <div className="flex items-center gap-1 text-[10px] text-blue-400">
                  <span>✓</span>
                  <span>Skip if Intune Compliant Device</span>
                </div>
              )}
              {alert.skipIfDrmmMatch && (
                <div className="flex items-center gap-1 text-[10px] text-blue-400">
                  <span>✓</span>
                  <span>Skip if DRMM Match</span>
                </div>
              )}
              {alert.skipIfInTenantBaseline && (
                <div className="flex items-center gap-1 text-[10px] text-blue-400">
                  <span>✓</span>
                  <span>Skip if IP in MSP Tenant Baseline</span>
                </div>
              )}
              {alert.skipIfInUserBaseline && (
                <div className="flex items-center gap-1 text-[10px] text-blue-400">
                  <span>✓</span>
                  <span>Skip if IP in User Baseline</span>
                </div>
              )}
              {alert.skipIfCtapTrust && (
                <div className="flex items-center gap-1 text-[10px] text-blue-400">
                  <span>✓</span>
                  <span>Skip if CTAP Inbound Trust</span>
                </div>
              )}
              {alert.skipIpv6PrefixMatch && (
                <div className="flex items-center gap-1 text-[10px] text-blue-400">
                  <span>✓</span>
                  <span>IPv6 /64 prefix match (modifier)</span>
                </div>
              )}
              {alert.overrideIfMaliciousIp && (
                <div className="flex items-center gap-1 text-[10px] text-amber-400">
                  <span>↺</span>
                  <span>Alert if a Known Malicious IP (override)</span>
                </div>
              )}
            </div>
          )}
          {alert.rawEventJson?.startsWith('blob:') && !hasBlobEvents && (
            <p className="mt-2 text-gray-600 italic">Raw event stored externally</p>
          )}
        </div>
      </div>
        );
      })()}

      {/* ── Device Posture (Directory + MDM) — lazy-fetched on expand ──
          Source the deviceId from the alert's own raw event when it has one (sign-in alerts).
          For non-sign-in events (Exchange / SharePoint / etc.) the pipeline attaches a correlated
          graph_signin JSON; that record carries deviceDetail.deviceId of the session the action came
          from, so the analyst can still see device posture for the inbox rule / file action / etc. */}
      {(() => {
        if (!alert.tenantId) return null;
        let devId = deviceDetail?.deviceId;
        if (!devId && alert.correlatedSignInJson) {
          try { devId = JSON.parse(alert.correlatedSignInJson)?.deviceDetail?.deviceId; } catch { /* ignore */ }
        }
        if (!devId) return null;
        return (
          <div className="px-3 py-3 border-t border-gray-800/60">
            <DevicePosturePanel tenantId={alert.tenantId} deviceId={devId} />
          </div>
        );
      })()}

      {/* ── Conditional Access Policies ── */}
      {caPolicies.length > 0 && (() => {
        const HIDDEN_RESULTS = new Set(['notApplied', 'notEnabled', 'reportOnlyNotApplied', 'reportOnlySuccess']);
        const hiddenCount = caPolicies.filter(p => HIDDEN_RESULTS.has(p.result)).length;
        const displayPolicies = showNotApplied ? caPolicies : caPolicies.filter(p => !HIDDEN_RESULTS.has(p.result));

        const resultBadgeClass = (result) => {
          if (result === 'success') return 'bg-green-900/40 text-green-300 border border-green-700/40';
          if (result === 'failure') return 'bg-red-900/40 text-red-300 border border-red-700/40';
          if (result === 'reportOnlySuccess' || result === 'reportOnlyFailure') return 'bg-purple-900/40 text-purple-300 border border-purple-700/40';
          return 'bg-gray-800 text-gray-500 border border-gray-700';
        };
        const resultLabel = (result) => {
          if (result === 'success') return 'Applied';
          if (result === 'failure') return 'Blocked';
          if (result === 'reportOnlySuccess') return 'Report (Pass)';
          if (result === 'reportOnlyFailure') return 'Report (Fail)';
          if (result === 'notApplied') return 'Not Applied';
          return result || '—';
        };

        return (
          <div className="px-6 py-3 border-t border-gray-800/60">
            <div className="flex items-center gap-2 mb-2">
              <p className="font-semibold text-violet-400 uppercase tracking-wide text-xs">
                Conditional Access Policies ({caPolicies.length})
              </p>
              {hiddenCount > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowNotApplied(v => !v); }}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-400 transition-colors"
                >
                  {showNotApplied ? `Hide ${hiddenCount} inactive` : `Show ${hiddenCount} inactive`}
                </button>
              )}
            </div>
            <div className="space-y-1">
              {displayPolicies.length === 0 && hiddenCount > 0 ? (
                <p className="text-xs text-gray-500 italic">All {hiddenCount} policies were inactive (not applied, not enabled, or report-only)</p>
              ) : (
                displayPolicies.map((policy, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${resultBadgeClass(policy.result)}`}>
                      {resultLabel(policy.result)}
                    </span>
                    <span className="text-gray-300">{policy.displayName || '(unnamed)'}</span>
                    {Array.isArray(policy.enforcedGrantControls) && policy.enforcedGrantControls.length > 0 && (
                      <span className="text-gray-500 text-[10px]">({policy.enforcedGrantControls.join(', ')})</span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Authentication App Policy Evaluation (graph_signin authenticationAppPolicyEvaluationDetails) ── */}
      {(() => {
        const details = rawEvent?.authenticationAppPolicyEvaluationDetails;
        if (!Array.isArray(details) || details.length === 0) return null;
        return (
          <div className="px-6 py-3 border-t border-gray-800/60">
            <div className="text-xs text-violet-400 mb-2 font-medium uppercase tracking-wide">
              Authentication App Policy Evaluation ({details.length})
            </div>
            <div className="space-y-1">
              {details.map((d, i) => {
                const label = humanizePolicyEval(d.policyName, d.authenticationEvaluation);
                const evalColor = evalTextColor(d.authenticationEvaluation);
                const statusDetail = humanizePolicyStatus(d.status);
                const showStatus = statusDetail && statusDetail !== 'unknown';
                return (
                  <div key={i} className="text-xs bg-gray-950/40 rounded px-3 py-1.5">
                    <span className={`font-medium ${evalColor}`}>{label}</span>
                    {showStatus && <span className="text-gray-500 ml-2 text-[10px]">— {statusDetail}</span>}
                    {d.applicationName && <span className="text-gray-600 ml-2 text-[10px]">({d.applicationName})</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Source (directory audit events — who initiated the change + their IP) ── */}
      {(() => {
        const initBy = rawEvent?.initiatedBy;
        if (!initBy) return null;
        const u = initBy.user;
        const a = initBy.app;
        if (!u && !a) return null;
        return (
          <div className="px-6 py-3 border-t border-gray-800/60">
            <div className="text-xs text-cyan-400 mb-2 font-medium uppercase tracking-wide">
              Source
            </div>
            <div className="bg-gray-950/40 rounded p-3 text-xs">
              {u && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">UPN: </span><span className="text-gray-300 font-mono">{u.userPrincipalName || '—'}</span></div>
                  <div><span className="text-gray-500">IP: </span><span className="text-gray-300 font-mono">{u.ipAddress || '—'}</span></div>
                </div>
              )}
              {a && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1">
                  <div><span className="text-gray-500">App Name: </span><span className="text-gray-300">{a.displayName || '—'}</span></div>
                  <div><span className="text-gray-500">App ID: </span><span className="text-gray-300 font-mono">{a.servicePrincipalId || a.appId || '—'}</span></div>
                  <div><span className="text-gray-500">Type: </span><span className="text-gray-300">Application</span></div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Target Resource (directory audit events — role assignments, app consent,
             group membership changes, etc.) ── */}
      {(() => {
        const targets = rawEvent?.targetResources;
        if (!Array.isArray(targets) || targets.length === 0) return null;
        const meaningful = targets.filter(t =>
          t.displayName ||
          t.userPrincipalName ||
          (Array.isArray(t.modifiedProperties) && t.modifiedProperties.length > 0)
        );
        if (meaningful.length === 0) return null;
        const fmt = (v) => {
          if (v === null || v === undefined) return 'null';
          if (v === '') return '""';
          if (typeof v !== 'string') return String(v);
          try { const parsed = JSON.parse(v); return typeof parsed === 'string' ? parsed : v; }
          catch { return v; }
        };
        return (
          <div className="px-6 py-3 border-t border-gray-800/60">
            <div className="text-xs text-amber-400 mb-2 font-medium uppercase tracking-wide">
              Target Resource{meaningful.length > 1 ? 's' : ''}
            </div>
            <div className="space-y-2">
              {meaningful.map((t, idx) => {
                const wk = t.modifiedProperties?.find(p => p.displayName === 'Role.WellKnownObjectName');
                const wkVal = wk ? fmt(wk.newValue) : null;
                return (
                  <div key={idx} className="bg-gray-950/40 rounded p-3 text-xs">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                      <div>
                        <span className="text-gray-500">Display Name: </span>
                        <span className={isConsentEvent ? 'text-gray-100 font-semibold text-sm' : 'text-gray-300'}>
                          {t.displayName || '—'}
                        </span>
                      </div>
                      <div><span className="text-gray-500">Object ID: </span><span className="text-gray-300 font-mono">{t.id || '—'}</span></div>
                      <div><span className="text-gray-500">UPN: </span><span className="text-gray-300 font-mono">{t.userPrincipalName || '—'}</span></div>
                      <div><span className="text-gray-500">Type: </span><span className="text-gray-300">{t.type || '—'}</span></div>
                    </div>
                    {!isConsentEvent && Array.isArray(t.modifiedProperties) && t.modifiedProperties.filter(mp => !HIDDEN_MODIFIED_PROPS.has(mp.displayName)).length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-800">
                        <div className="text-gray-500 mb-1">Modified Properties</div>
                        <div className="space-y-1.5 font-mono">
                          {t.modifiedProperties.filter(mp => !HIDDEN_MODIFIED_PROPS.has(mp.displayName)).map((mp, mpIdx) => {
                            // When both old/new are JSON objects, render a structured color-coded diff
                            const oldParsed = tryParseAsObject(mp.oldValue);
                            const newParsed = tryParseAsObject(mp.newValue);
                            if (oldParsed && newParsed) {
                              const diff = computeJsonDiff(oldParsed, newParsed);
                              const total = diff.changed.length + diff.added.length + diff.removed.length;
                              if (total === 0) return (
                                <div key={mpIdx} className="break-all">
                                  <span className="text-gray-400">{mp.displayName}:</span>{' '}
                                  <span className="text-gray-600 italic">no changes detected</span>
                                </div>
                              );
                              return (
                                <div key={mpIdx}>
                                  <div className="text-gray-400 mb-1">{mp.displayName} <span className="text-gray-600">({total} change{total !== 1 ? 's' : ''})</span></div>
                                  <div className="ml-3 space-y-1">
                                    {diff.changed.map((c) => (
                                      c.type === 'array' ? (
                                        <div key={c.path} className="space-y-0.5">
                                          <span className="text-gray-500">{c.path}:</span>
                                          {c.addedItems.map((item, i) => (
                                            <div key={`a${i}`} className="ml-3 flex items-start gap-2 flex-wrap">
                                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-emerald-900/60 text-emerald-300 border border-emerald-700/60">Added</span>
                                              <ResolvedId value={item} names={dirNames} valueClass="text-emerald-400 font-mono" {...dirResolveProps} />
                                            </div>
                                          ))}
                                          {c.removedItems.map((item, i) => (
                                            <div key={`r${i}`} className="ml-3 flex items-start gap-2 flex-wrap">
                                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-red-900/60 text-red-300 border border-red-700/60">Removed</span>
                                              <ResolvedId value={item} names={dirNames} valueClass="text-red-400/70 font-mono" {...dirResolveProps} />
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div key={c.path} className="flex items-start gap-2 flex-wrap break-all">
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-amber-900/60 text-amber-300 border border-amber-700/60">Changed</span>
                                          <span className="text-gray-500">{c.path}:</span>
                                          <ResolvedId value={c.oldVal} names={dirNames} valueClass="text-red-400/70" {...dirResolveProps} />
                                          <span className="text-gray-600">→</span>
                                          <ResolvedId value={c.newVal} names={dirNames} valueClass="text-emerald-400" {...dirResolveProps} />
                                        </div>
                                      )
                                    ))}
                                    {diff.added.map((a) => (
                                      <div key={a.path} className="flex items-start gap-2 flex-wrap break-all">
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-emerald-900/60 text-emerald-300 border border-emerald-700/60">Added</span>
                                        <span className="text-gray-400">{a.path}:</span>
                                        <ResolvedId value={a.value} names={dirNames} valueClass="text-emerald-400 font-mono" {...dirResolveProps} />
                                      </div>
                                    ))}
                                    {diff.removed.map((r) => (
                                      <div key={r.path} className="flex items-start gap-2 flex-wrap break-all">
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 bg-red-900/60 text-red-300 border border-red-700/60">Removed</span>
                                        <span className="text-gray-400">{r.path}:</span>
                                        <ResolvedId value={r.value} names={dirNames} valueClass="text-red-400/70 font-mono" {...dirResolveProps} />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            }

                            // One-sided object change: a whole object was created
                            // (old empty → new object) or deleted (old object → new empty).
                            // The classic case is a CA policy SoftDelete, where newValue
                            // is the literal "null". Render the object's settings as a
                            // structured, color-coded key→value list instead of dumping
                            // the raw JSON string inline.
                            const createdSide = newParsed && isNullishVal(mp.oldValue);
                            const deletedSide = oldParsed && isNullishVal(mp.newValue);
                            if (createdSide || deletedSide) {
                              const obj = createdSide ? newParsed : oldParsed;
                              const flat = flattenObj(obj);
                              const entries = Object.entries(flat)
                                .filter(([p]) => !DIFF_SKIP_ROOTS.has(p.split('.')[0]));
                              const valClass = createdSide ? 'text-emerald-400' : 'text-red-400/70';
                              return (
                                <div key={mpIdx}>
                                  <div className="text-gray-400 mb-1 flex items-center gap-2 flex-wrap">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${createdSide
                                      ? 'bg-emerald-900/60 text-emerald-300 border border-emerald-700/60'
                                      : 'bg-red-900/60 text-red-300 border border-red-700/60'}`}>
                                      {createdSide ? 'Created' : 'Deleted'}
                                    </span>
                                    <span>{mp.displayName}</span>
                                    <span className="text-gray-600">({entries.length} setting{entries.length !== 1 ? 's' : ''})</span>
                                  </div>
                                  <div className="ml-3 space-y-0.5">
                                    {entries.map(([path, value]) => (
                                      <div key={path} className="flex items-start gap-2 flex-wrap break-all">
                                        <span className="text-gray-500">{path}:</span>
                                        <ResolvedId value={value} names={dirNames} valueClass={`${valClass} font-mono`} {...dirResolveProps} />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            }

                            // Simple diff for scalar properties
                            const oldV = fmt(mp.oldValue);
                            let newV = fmt(mp.newValue);
                            if (mp.displayName === 'Role.DisplayName' && wkVal && wkVal !== 'null') {
                              newV = `${newV} (${wkVal})`;
                            }
                            return (
                              <div key={mpIdx} className="break-all">
                                <span className="text-gray-400">{mp.displayName}:</span>{' '}
                                <span className="text-gray-500">From</span>{' '}
                                <span className="text-gray-300">"{oldV}"</span>{' '}
                                <span className="text-gray-500">to</span>{' '}
                                <span className="text-gray-300">"{newV}"</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Permissions Granted (consent-to-app — handles both correlated-events shape
             and ConsentAction.Permissions blob shape; see extractConsentPermissions) ── */}
      {permissions.length > 0 && (
        <div className="px-6 py-3 border-t border-gray-800/60">
          <div className="text-xs text-violet-400 mb-2 font-medium uppercase tracking-wide">
            Permissions Granted
          </div>
          <div className="bg-gray-950/40 rounded p-3 text-xs space-y-1">
            {permissions.map((p, i) => {
              const statusClass = p.status === 'new'
                ? 'bg-emerald-900/60 text-emerald-300 border border-emerald-700/60'
                : p.status === 'revoked'
                ? 'bg-red-900/60 text-red-300 border border-red-700/60'
                : 'bg-gray-800/80 text-gray-400 border border-gray-700/60';
              const statusLabel = p.status === 'new' ? 'New' : p.status === 'revoked' ? 'Revoked' : 'Existing';
              return (
                <div key={i} className="flex items-start gap-2 flex-wrap">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${statusClass}`}>
                    {statusLabel}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                    p.kind === 'Application'
                      ? 'bg-rose-900/50 text-rose-300 border border-rose-700/60'
                      : 'bg-sky-900/50 text-sky-300 border border-sky-700/60'
                  }`}>
                    {p.kind}
                  </span>
                  {p.api && <span className="text-gray-500">{p.api}:</span>}
                  <span className={`font-mono font-semibold ${p.status === 'revoked' ? 'text-gray-500 line-through' : 'text-gray-200'}`}>{p.scope}</span>
                  {p.description && <span className="text-gray-400">— {p.description}</span>}
                  {p.consentType && <span className="text-gray-500 text-[10px]">({p.consentType})</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── IP Enrichment JSON ── */}
      {alert.ipEnrichmentJson && (
        <div className="px-6 py-3 border-t border-gray-800/60">
          <RawEventJsonPanel
            title={`IP Enrichment Record — ${stripPort(alert.clientIp)}`}
            rawEventJson={alert.ipEnrichmentJson}
          />
        </div>
      )}

      {/* ── Raw Event JSON ── always last in the expanded row */}
      {alert.rawEventJson && !alert.rawEventJson.startsWith('blob:') && (
        <div className="px-6 py-3 border-t border-gray-800/60">
          <RawEventJsonPanel
            title="Raw Event JSON"
            rawEventJson={alert.rawEventJson}
          />
        </div>
      )}
    </div>
  );
}

// ── Raw record modal ─────────────────────────────────────────────────────────

function RawAlertModal({ alert, onClose }) {
  const [copied, setCopied] = useState(false);

  // Build a display object: parse rawEventJson and correlatedSignInJson if they're inline JSON
  const displayObj = useMemo(() => {
    const obj = { ...alert };
    if (obj.rawEventJson && !obj.rawEventJson.startsWith('blob:')) {
      try { obj.rawEventJson = JSON.parse(obj.rawEventJson); } catch { /* keep as string */ }
    }
    if (obj.correlatedSignInJson && !obj.correlatedSignInJson.startsWith('blob:')) {
      try { obj.correlatedSignInJson = JSON.parse(obj.correlatedSignInJson); } catch { /* keep as string */ }
    }
    if (obj.baselineDeviation) {
      try { obj.baselineDeviation = JSON.parse(obj.baselineDeviation); } catch { /* keep as string */ }
    }
    return obj;
  }, [alert]);

  const jsonStr = useMemo(() => JSON.stringify(displayObj, null, 2), [displayObj]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonStr).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="flex items-center gap-3 min-w-0">
            <Badge tone={severityTone(alert.severity)} dot>{sentence(alert.severity)}</Badge>
            <span className="text-sm text-gray-300 truncate">{alert.ruleName}</span>
            <span className="text-xs text-gray-500 truncate" title={actorTitle(alert)}>{displayActor(alert)}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors"
            >
              <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-200 transition-colors">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-auto p-4">
          <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap break-all">
            {jsonStr}
          </pre>
        </div>
      </div>
    </div>
  );
}

// ── Close-reason modal ───────────────────────────────────────────────────────
// Shown when an analyst closes an alert; the reason is required so triage
// decisions stay auditable. Quick-pick chips prefill common dispositions.

const CLOSE_REASON_PRESETS = ['False positive', 'Benign true positive', 'Remediated'];
const CLIENT_NOT_MONITORED_NOTE = 'client not monitored';

function CloseReasonModal({ alert, onCancel, onConfirm, submitting }) {
  const [reason, setReason] = useState('');
  // A linked live PSA ticket (resolved before the modal opened) → offer to close
  // it too, defaulted on.
  const ticket = alert?.ticket || null;
  const [closeTicket, setCloseTicket] = useState(true);
  // Optional PSA time entry — only offered when the caller has a PSA user mapping
  // for this ticket's MSP (server-confirmed eligibility). The close reason is reused
  // as the time-entry summary, so no extra text field is needed.
  const timeEligible = !!ticket?.timeEntry?.eligible;
  const timeResourceName = ticket?.timeEntry?.psaResourceName || '';
  const [logTime, setLogTime] = useState(false);
  const [minutes, setMinutes] = useState('');

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  const trimmed = reason.trim();
  const minutesNum = parseInt(minutes, 10);
  const minutesValid = Number.isFinite(minutesNum) && minutesNum > 0;
  // Time is logged only when: ticket close is on, the caller is eligible, the
  // toggle is on, and a positive whole-minute value was entered.
  const showTimeEntry = !!ticket && closeTicket && timeEligible;
  const effectiveMinutes = showTimeEntry && logTime && minutesValid ? minutesNum : 0;
  const blockConfirm = showTimeEntry && logTime && !minutesValid;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" />
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-lg shadow-2xl w-full max-w-lg flex flex-col mx-4"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <span className="text-sm text-gray-200">Close alert — add a resolution note</span>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-200 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {alert?.ruleName && (
            <p className="text-xs text-gray-500 truncate" title={alert.ruleName}>{alert.ruleName}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {CLOSE_REASON_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setReason((r) => (r ? `${p} — ${r}` : `${p} — `))}
                className="px-2.5 py-1 text-xs rounded border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors"
              >
                {p}
              </button>
            ))}
            {/* A complete note, not a prefix: the alert is for a tenant whose
                client is not monitored, so there is nothing to add. */}
            <button
              type="button"
              onClick={() => setReason(CLIENT_NOT_MONITORED_NOTE)}
              className="px-2.5 py-1 text-xs rounded border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors"
            >
              Client not monitored
            </button>
          </div>
          <textarea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="Why is this being closed? (e.g. false positive — known admin IP; or actions taken to remediate)"
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-blue-500"
          />
          {!trimmed && (
            <p className="text-[11px] text-amber-400/90">
              A resolution note is required to close this alert — type one above or pick a preset.
            </p>
          )}
          {alert?.ticketLoading && (
            <p className="text-[11px] text-gray-500">Checking for a linked PSA ticket…</p>
          )}
          {ticket && (
            <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer select-none pt-1">
              <input
                type="checkbox"
                checked={closeTicket}
                onChange={(e) => setCloseTicket(e.target.checked)}
                className="mt-0.5 accent-blue-500"
              />
              <span>
                Also close linked {psaSystemLabel(ticket.ticketingSystem)} ticket{' '}
                <span className="font-mono text-gray-200">#{ticket.ticketNumber || ticket.ticketId}</span>
                {' '}— adds the resolution note, then sets it complete.
              </span>
            </label>
          )}
          {!alert?.ticketLoading && alert?.checkedTicket && !ticket && alert?.ticketingMapped && (
            <p className="text-[11px] text-gray-400 border border-gray-700/60 rounded px-2.5 py-2 bg-gray-800/40 leading-relaxed">
              {alert?.ticketingConfigured ? (
                <>
                  No PSA ticket is linked to this alert. PSA routing for this tenant was
                  configured <span className="text-gray-300">after</span> this alert was
                  generated, so no ticket was opened — time can't be logged here. New alerts
                  for this tenant will open tickets automatically.
                </>
              ) : (
                <>
                  No PSA ticket is linked to this alert. PSA ticket creation for this tenant
                  is currently <span className="text-gray-300">disabled</span>, so no ticket
                  was opened and time can't be logged here.
                </>
              )}
            </p>
          )}
          {showTimeEntry && (
            <div className="pl-6 pt-1 space-y-2">
              <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={logTime}
                  onChange={(e) => setLogTime(e.target.checked)}
                  className="mt-0.5 accent-blue-500"
                />
                <span>
                  Log time on the ticket as{' '}
                  <span className="text-gray-200">{timeResourceName || 'your PSA user'}</span>
                  {' '}— uses the resolution note above as the time-entry summary.
                </span>
              </label>
              {logTime && (
                <div className="flex items-center gap-2 pl-6">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    placeholder="Minutes"
                    className="w-24 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-200 focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-xs text-gray-500">minutes</span>
                  {!minutesValid && (
                    <span className="text-[11px] text-amber-400/90">Enter a whole number of minutes &gt; 0.</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-700">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 text-sm rounded text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(trimmed, ticket ? closeTicket : false, effectiveMinutes)}
            disabled={!trimmed || submitting || blockConfirm || alert?.ticketLoading}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 transition-colors"
          >
            {submitting ? 'Closing…' : (alert?.ticketLoading ? 'Checking ticket…' : 'Close alert')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Alert row ────────────────────────────────────────────────────────────────

function AlertRow({ alert, compromised, onStatusChange, onSigninOutcomeResolved, onLinkedAuditResolved, onDirNameResolved, updatingId, showTenant, tenantName, onShowRaw, tzIana, isAdmin, onOpenAllowlistModal, allowlistRuleIds, onOpenUserAppModal, userAppPairRuleIds, onOpenGroupAppModal, groupAppRuleIds, onOpenSuppressTitleModal, onOpenSuppressDescModal, onOpenSuppressActorModal, passThroughRuleMeta, onOpenBenignIpModal, passthroughIpRuleIds, forceExpandId }) {
  const isDeepTarget = !!forceExpandId && (alert.alertId || alert.rowKey) === forceExpandId;
  const [expanded, setExpanded] = useState(isDeepTarget);
  const rowRef = useRef(null);
  const scrolledRef = useRef(false);
  // Scroll the deep-linked row into view once after it mounts/expands.
  useEffect(() => {
    if (isDeepTarget && !scrolledRef.current && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
      scrolledRef.current = true;
    }
  }, [isDeepTarget]);
  const isUpdating = updatingId === alert.alertId;
  // Quick-action "Allowlist" button shows on any mailbox-pair-allowlist-enabled
  // rule's alerts for admins (toggle-driven set, v1.70.0).
  const showAllowlistAction = isAdmin && !alert._isTestResult && !!allowlistRuleIds && allowlistRuleIds.has(alert.ruleId);
  // Quick-action "Suppress (user, app) pair" shows on any
  // skip_if_in_user_app_pair_allowlist-enabled rule's alerts for admins (v1.83.0).
  // Unlike the mailbox-pair action this INCLUDES test-result rows: the launch rule
  // is held in TEST so the allowlist can be seeded from test-run matches before the
  // rule is promoted to PROD (the Add endpoint gates on the rule toggle, not on
  // test-vs-prod). Test rows carry applicationId + appDisplayName (GetTestResults).
  const showUserAppAction = isAdmin && !!userAppPairRuleIds && userAppPairRuleIds.has(alert.ruleId);
  // Quick-action "Suppress (group, app) pair" shows on any
  // skip_if_in_group_app_allowlist-enabled rule's alerts for admins (v1.85.1). Like the
  // user/app action it INCLUDES test-result rows (the launch rule is held in TEST so the
  // allowlist can be seeded from test-run matches before promotion).
  const showGroupAppAction = isAdmin && !!groupAppRuleIds && groupAppRuleIds.has(alert.ruleId);
  // Quick-action "Suppress this alert title" shows on Defender security_alert
  // pass-through alerts for admins (v1.101.0). passThroughRuleMeta is the firing rule's
  // {ruleName, includedTenantIds, excludedTenantIds}, present only for pass-through rules.
  const showSuppressTitleAction = isAdmin && !!passThroughRuleMeta;
  // Description quick-suppress: same gate, prefilled from the security_alert payload's
  // $.description / $.detectorId (present only on parsed, non-spilled rawEvent).
  const showSuppressDescAction = isAdmin && !!passThroughRuleMeta;
  // Quick-action "Add IP to benign-IP allowlist" shows on any rule with the
  // skip_if_in_defender_passthrough_ip_allowlist toggle on (v1.114.0). The allowlist is a
  // single GLOBAL exact-IP list, so this INCLUDES test-result rows (seed from test runs)
  // and only shows when the alert actually carries a source IP to add.
  const showBenignIpAction = isAdmin && !!passthroughIpRuleIds && passthroughIpRuleIds.has(alert.ruleId) && !!stripPort(alert.clientIp);
  // AlertRow (unlike AlertDetail) has no parsed rawEvent in scope, so parse the
  // alert's inline rawEventJson here — only when the action is actually shown
  // (pass-through alert + admin). The security_alert payload carries $.description
  // and $.detectorId at the top level.
  const descPrefill = useMemo(() => {
    if (!showSuppressDescAction) return { description: '', detectorId: '' };
    let rawEvent = null;
    if (alert.rawEventJson && !String(alert.rawEventJson).startsWith('blob:')) {
      try { rawEvent = JSON.parse(alert.rawEventJson); } catch { rawEvent = null; }
    }
    return {
      description: (rawEvent && typeof rawEvent.description === 'string') ? rawEvent.description : '',
      detectorId: (rawEvent && typeof rawEvent.detectorId === 'string') ? rawEvent.detectorId : '',
    };
  }, [showSuppressDescAction, alert.rawEventJson]);
  // Actor quick-suppress (v1.123.2): pre-fill from the security_alert payload's first
  // evidence actor — coalesce userAccount.userPrincipalName -> primaryAddress -> upn,
  // matching exactly what the pipeline keys on. Only shown on pass-through rules for
  // admins AND when the alert actually carries an evidence actor to suppress.
  const actorPrefill = useMemo(() => {
    if (!(isAdmin && !!passThroughRuleMeta)) return { actor: '' };
    let rawEvent = null;
    if (alert.rawEventJson && !String(alert.rawEventJson).startsWith('blob:')) {
      try { rawEvent = JSON.parse(alert.rawEventJson); } catch { rawEvent = null; }
    }
    let actor = '';
    const ev = rawEvent && Array.isArray(rawEvent.evidence) ? rawEvent.evidence : [];
    for (const e of ev) {
      const a = (e?.userAccount?.userPrincipalName || e?.primaryAddress || e?.upn || '').toString().trim();
      if (a) { actor = a; break; }
    }
    return { actor };
  }, [isAdmin, passThroughRuleMeta, alert.rawEventJson]);
  const showSuppressActorAction = isAdmin && !!passThroughRuleMeta && !!actorPrefill.actor;

  return (
    <>
      <tr
        ref={rowRef}
        className={`border-b border-gray-800 hover:bg-gray-900/40 transition-colors cursor-pointer ${
          alert.status === 'closed' ? 'opacity-60' : ''
        } ${compromised ? 'border-l-2 border-l-red-600 bg-red-950/10' : (alert._isTestResult ? 'border-l-2 border-l-amber-600/60' : '')} ${
          isDeepTarget ? 'ring-1 ring-blue-500/50' : ''
        }`}
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Expand toggle */}
        <td className="pl-3 pr-1 py-3 w-8">
          {expanded
            ? <ChevronDownIcon className="h-3.5 w-3.5 text-gray-600" />
            : <ChevronRightIcon className="h-3.5 w-3.5 text-gray-600" />}
        </td>

        {/* Raw record button */}
        <td className="px-1 py-3 w-8" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onShowRaw(alert)}
            className="p-0.5 text-gray-600 hover:text-blue-400 transition-colors"
            title="View raw record"
          >
            <CodeBracketIcon className="h-3.5 w-3.5" />
          </button>
        </td>

        {/* Alert time */}
        <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">
          {formatTs(alert.alertTime, tzIana)}
        </td>

        {/* Event time */}
        <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">
          {formatTs(alert.eventTime, tzIana)}
        </td>

        {/* Severity */}
        <td className="px-3 py-3">
          <Badge tone={severityTone(alert.severity)}>
            {severityIcon(alert.severity)}
            {sentence(alert.severity)}
          </Badge>
        </td>

        {/* Rule name */}
        <td className="px-3 py-3 text-sm text-gray-200 overflow-hidden">
          <span className="block truncate">{alert.ruleName}</span>
          <span className="text-xs text-gray-500 block truncate">{alert.logType}</span>
          {compromised && (
            <span
              className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-950/60 border border-red-600/60 text-red-300 whitespace-nowrap"
              title="The correlated sign-in confirms the password was valid (or the sign-in succeeded). Credential compromised — see the expanded row."
            >
              <LockClosedIcon className="h-3 w-3" /> PWD COMPROMISED
            </span>
          )}
        </td>

        {/* User */}
        <td className="px-3 py-3 text-xs text-gray-300 overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <span className="group flex items-center gap-0.5 font-mono truncate" title={actorTitle(alert)}>
            <span className="truncate">{displayActor(alert) || '—'}</span>
            <CopyButton text={copyActor(alert)} />
          </span>
          {showTenant && tenantName && (
            <span className="text-gray-600 truncate block">{tenantName}</span>
          )}
        </td>

        {/* IP + country */}
        <td className="px-3 py-3 text-xs text-gray-400 overflow-hidden">
          <span className="group flex items-center gap-0.5 font-mono truncate" title={stripPort(alert.clientIp) || ''}>
            <span className="truncate">{stripPort(alert.clientIp) || '—'}</span>
            {alert.clientIp && <CopyButton text={stripPort(alert.clientIp)} />}
          </span>
          <span className="text-gray-500 truncate block">{alert.ipCountry || ''}</span>
        </td>

        {/* Status badge */}
        <td className="px-3 py-3">
          {alert._isTestResult ? (
            <Badge tone="warning">
              <BeakerIcon className="h-3 w-3" />
              Test
            </Badge>
          ) : (
            <StatusDropdown
              status={alert.status}
              isUpdating={isUpdating}
              onStatusChange={(s) => onStatusChange(alert.alertId, alert.tenantId, s)}
            />
          )}
        </td>

        {/* Actions. Session Trace lives inside the expanded row's panel; status
            changes live on the Status badge dropdown — so this column only hosts
            quick-suppress affordances. The mailbox-pair action is prod-only (its
            gate excludes test rows); the (user, app) action ALSO shows on test
            rows, because its launch rule is held in TEST precisely so the
            allowlist can be seeded from test-run matches before promotion. */}
        <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-0.5">
            {showAllowlistAction && (
              <button
                onClick={() => onOpenAllowlistModal(alert)}
                title="Suppress: allowlist mailbox pair(s) from this alert"
                className="p-1 rounded text-emerald-400 hover:bg-emerald-900/30 transition-colors"
              >
                <BellSlashIcon className="h-4 w-4" />
              </button>
            )}
            {showUserAppAction && (
              <button
                onClick={() => onOpenUserAppModal(alert)}
                title="Suppress: allowlist this (user, application) pair from this alert"
                className="p-1 rounded text-sky-400 hover:bg-sky-900/30 transition-colors"
              >
                <BellSlashIcon className="h-4 w-4" />
              </button>
            )}
            {showGroupAppAction && (
              <button
                onClick={() => onOpenGroupAppModal(alert)}
                title="Suppress: allowlist a (group, application) pair from this alert"
                className="p-1 rounded text-indigo-400 hover:bg-indigo-900/30 transition-colors"
              >
                <BellSlashIcon className="h-4 w-4" />
              </button>
            )}
            {showSuppressTitleAction && (
              <button
                onClick={() => onOpenSuppressTitleModal(alert, passThroughRuleMeta)}
                title="Suppress: stop this Defender alert title on this rule"
                className="p-1 rounded text-rose-400 hover:bg-rose-900/30 transition-colors"
              >
                <BellSlashIcon className="h-4 w-4" />
              </button>
            )}
            {showSuppressDescAction && (
              <button
                onClick={() => onOpenSuppressDescModal(alert, passThroughRuleMeta, descPrefill)}
                title="Suppress: stop this Defender alert description on this rule"
                className="p-1 rounded text-orange-400 hover:bg-orange-900/30 transition-colors"
              >
                <BellSlashIcon className="h-4 w-4" />
              </button>
            )}
            {showSuppressActorAction && (
              <button
                onClick={() => onOpenSuppressActorModal(alert, passThroughRuleMeta, actorPrefill)}
                title="Suppress: stop this Defender alert actor (service account) on this rule"
                className="p-1 rounded text-violet-400 hover:bg-violet-900/30 transition-colors"
              >
                <BellSlashIcon className="h-4 w-4" />
              </button>
            )}
            {showBenignIpAction && (
              <button
                onClick={() => onOpenBenignIpModal(alert)}
                title="Suppress: add this IP to the benign-IP allowlist"
                className="p-1 rounded text-teal-400 hover:bg-teal-900/30 transition-colors"
              >
                <BellSlashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-gray-900/50 border-b-2 border-gray-700">
          <td colSpan={10} className="pl-10 pr-0 py-0 border-l-4 border-blue-600/40">
            <AlertDetail alert={alert} tzIana={tzIana} onSigninOutcomeResolved={onSigninOutcomeResolved} onLinkedAuditResolved={onLinkedAuditResolved} onDirNameResolved={onDirNameResolved} />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Inline column header filter ──────────────────────────────────────────────

function ColFilter({ value, onChange, options, allLabel = 'All', labelFn }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className="w-full mt-1 bg-gray-800 border border-gray-700 rounded px-1.5 py-0.5 text-xs text-gray-300 focus:outline-none focus:border-blue-500 cursor-pointer"
    >
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o} value={o}>{labelFn ? labelFn(o) : o}</option>
      ))}
    </select>
  );
}

// ── Clickable status dropdown (set an alert's status inline) ──────────────────
// The three editable statuses the backend (api/UpdateAlert) accepts. `test` is a
// synthetic value for test-result rows and is intentionally excluded here.
const EDITABLE_STATUS_OPTIONS = ['new', 'acknowledged', 'closed'];

function StatusDropdown({ status, isUpdating, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Position the menu in a portal (fixed coords) so it can't be clipped by the
  // table's overflow container; flip upward when there isn't room below.
  const updateCoords = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuH = EDITABLE_STATUS_OPTIONS.length * 34 + 10;
    const openUp = (r.bottom + menuH + 8) > window.innerHeight && r.top > menuH;
    setCoords({
      left: r.left,
      top: openUp ? Math.max(8, r.top - menuH - 4) : r.bottom + 4,
      minWidth: Math.max(r.width, 160),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // A fixed-positioned menu would detach on scroll/resize — close instead of chase.
    const onReflow = () => setOpen(false);
    document.addEventListener('mousedown', onDocDown);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open) updateCoords();
    setOpen((o) => !o);
  };

  const select = (s) => {
    setOpen(false);
    if (s !== status) onStatusChange(s);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        disabled={isUpdating}
        title="Change status"
        className="flex items-center gap-1 disabled:opacity-40 cursor-pointer focus:outline-none"
      >
        <Badge tone={alertStatusTone(status)} dot>{statusLabel(status)}</Badge>
        <ChevronDownIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left, minWidth: coords.minWidth }}
          className="z-50 rounded-md border border-gray-700 bg-gray-800 shadow-lg py-1"
        >
          {EDITABLE_STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={(e) => { e.stopPropagation(); select(s); }}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-gray-700/60 transition-colors"
            >
              <Badge tone={alertStatusTone(s)} dot>{statusLabel(s)}</Badge>
              {s === status && <CheckCircleIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low', 'informational'];
const STATUS_OPTIONS   = ['new', 'acknowledged', 'closed', 'test'];
const DEFAULT_PAGE_SIZE = 10;

export default function AlertsPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  // Deep link (/alerts/:tenantId/:alertId) from the Notification-Dispatcher.
  // When present we pre-select the tenant, widen the window so an older/closed
  // alert resolves, jump to its page, and auto-expand the row. Server-side MSP
  // auth (Stop-IfMspDenied in GetAlerts) still gates the data — an unauthorized
  // tenant returns nothing, so the row simply won't be found (graceful banner).
  const { tenantId: deepTenantId, alertId: deepAlertId } = useParams();
  const deepLinkActive = !!(deepTenantId && deepAlertId);
  const [deepNotFound, setDeepNotFound] = useState(false);
  const deepPageHandledRef = useRef(false);
  // Deep links load ONLY the linked alert (point read) for speed; the analyst
  // can opt into the full tenant list. False = single-alert view.
  const [loadFullList, setLoadFullList] = useState(false);
  // v1.60.0 quick-action: alert opened in the mailbox-pair allowlist modal.
  const [allowlistModalAlert, setAllowlistModalAlert] = useState(null);
  const [userAppPairModalAlert, setUserAppPairModalAlert] = useState(null);
  const [groupAppModalAlert, setGroupAppModalAlert] = useState(null);
  // v1.101.0 quick-action: alert opened in the Defender alert-title suppress modal.
  // Holds { alert, meta } where meta is the firing rule's scope (for the blast-radius line).
  const [suppressTitleModal, setSuppressTitleModal] = useState(null);
  const [suppressDescModal, setSuppressDescModal] = useState(null);
  const [suppressActorModal, setSuppressActorModal] = useState(null);
  // v1.114.0 quick-action: alert opened in the "add IP to benign-IP allowlist" modal.
  const [benignIpModalAlert, setBenignIpModalAlert] = useState(null);
  // Close-reason capture: alert pending a "closed" transition awaiting its note.
  const [pendingClose, setPendingClose] = useState(null);
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  // Transient toast for the PSA ticket-close outcome (auto-dismisses).
  const [ticketNotice, setTicketNotice] = useState(null);

  const [tenants, setTenants]               = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [tenantsLoading, setTenantsLoading] = useState(true);

  const [alerts, setAlerts]                 = useState([]);
  const [alertsLoading, setAlertsLoading]   = useState(false);
  const [alertsError, setAlertsError]       = useState(null);

  const [statusFilter, setStatusFilter]     = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [ruleFilter, setRuleFilter]         = useState('');
  const [userFilter, setUserFilter]         = useState('');
  const [search, setSearch]                 = useState('');
  const [showClosed, setShowClosed]         = useState(false);
  // Date range filter — defaults to the last 1 day (today-1 → today, local time),
  // EXCEPT on Mondays where it defaults to the last 3 days (3d preset) so the
  // weekend's alerts are in view. Empty strings mean "no bound" (All).
  const [dateFrom, setDateFrom]             = useState(() => daysAgoYmd(new Date().getDay() === 1 ? 3 : 1));
  const [dateTo, setDateTo]                 = useState(() => todayYmd());

  // Test results state
  const [testResults, setTestResults]       = useState([]);
  const [testLoading, setTestLoading]       = useState(false);
  const [showTestAlerts, setShowTestAlerts] = useState(false);

  // Timezone
  const [selectedTz, setSelectedTz] = useState(getDefaultTimezone);
  const tzObj = TIMEZONE_OPTIONS.find(t => t.id === selectedTz) || TIMEZONE_OPTIONS[0];
  const handleTzChange = (tz) => {
    setSelectedTz(tz);
    try { localStorage.setItem(TZ_STORAGE_KEY, tz); } catch { /* ignore */ }
  };

  const [updatingId, setUpdatingId]         = useState(null);
  const [page, setPage]                     = useState(1);

  // Resizable column widths (position-indexed; covers the 9 data columns,
  // excludes the 2 leading icon columns).
  const { colWidths, thRefs, tableRef, startResize } = useResizableCols(ALERT_COL_DEFAULTS);
  const alertTableWidth = ALERT_ICON_COL_W * 2 + colWidths.reduce((a, b) => a + b, 0);
  const [pageSize, setPageSize]             = useState(DEFAULT_PAGE_SIZE);
  const [rawAlert, setRawAlert]             = useState(null);

  // Sort state — default: Event Time descending
  const [sortColumn, setSortColumn]         = useState('eventTime');
  const [sortDirection, setSortDirection]   = useState('desc');

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortColumn !== col) return null;
    return sortDirection === 'asc'
      ? <ChevronUpIcon className="h-3 w-3 text-blue-400 inline ml-0.5" />
      : <ChevronDownIcon className="h-3 w-3 text-blue-400 inline ml-0.5" />;
  };

  // Load tenants on mount
  useEffect(() => {
    fetchTenants()
      .then((data) => {
        const active = (Array.isArray(data) ? data : []).filter((t) => t.Status !== 'inactive');
        setTenants(active);
      })
      .catch(() => {})
      .finally(() => setTenantsLoading(false));
  }, []);

  // Mailbox-pair allowlist quick-action: discover which rules have the toggle on
  // (admin-only endpoint; the button is admin-gated anyway). Replaces the old
  // hardcoded rule-008 GUID so the action follows the rule definition (v1.70.0).
  const [allowlistRuleIds, setAllowlistRuleIds] = useState(() => new Set());
  // (user, application) pair allowlist quick-action: rules with the
  // skip_if_in_user_app_pair_allowlist toggle on (v1.83.0).
  const [userAppPairRuleIds, setUserAppPairRuleIds] = useState(() => new Set());
  // (group, application) pair allowlist quick-action: rules with the
  // skip_if_in_group_app_allowlist toggle on (v1.85.1).
  const [groupAppRuleIds, setGroupAppRuleIds] = useState(() => new Set());
  // Defender pass-through rules: ruleId -> { ruleName, includedTenantIds, excludedTenantIds }.
  // Drives the "Suppress this alert title" action + its blast-radius line (v1.101.0).
  const [passThroughRules, setPassThroughRules] = useState(() => ({}));
  // Benign-IP allowlist quick-action: rules with the
  // skip_if_in_defender_passthrough_ip_allowlist toggle on (v1.114.0).
  const [passthroughIpRuleIds, setPassthroughIpRuleIds] = useState(() => new Set());
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    fetchDetectionRules()
      .then((rules) => {
        if (cancelled) return;
        const list = Array.isArray(rules) ? rules : [];
        setAllowlistRuleIds(new Set(list.filter((r) => r.skipIfInMailboxPairAllowlist === true).map((r) => r.ruleId)));
        setUserAppPairRuleIds(new Set(list.filter((r) => r.skipIfInUserAppPairAllowlist === true).map((r) => r.ruleId)));
        setGroupAppRuleIds(new Set(list.filter((r) => r.skipIfInGroupAppAllowlist === true).map((r) => r.ruleId)));
        setPassthroughIpRuleIds(new Set(list.filter((r) => r.skipIfInDefenderPassthroughIpAllowlist === true).map((r) => r.ruleId)));
        const ptMap = {};
        list.filter((r) => r.pythonFunctionName === DEFENDER_PASSTHROUGH_FN).forEach((r) => {
          ptMap[r.ruleId] = {
            ruleName: r.ruleName,
            includedTenantIds: r.includedTenantIds || '',
            excludedTenantIds: r.excludedTenantIds || '',
          };
        });
        setPassThroughRules(ptMap);
      })
      .catch(() => { /* best-effort — button just won't show */ });
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Apply deep-link view settings once: focus the tenant, show closed alerts,
  // and widen the date range to All (hoursForApi=0) so an older alert loads.
  useEffect(() => {
    if (!deepLinkActive) return;
    setSelectedTenant(deepTenantId);
    setShowClosed(true);
    setDateFrom('');
    setDateTo('');
  }, [deepLinkActive, deepTenantId]);

  // Server-side hours cutoff derived from the active date range. The frontend
  // still does the final OR(alertTime, eventTime) filter client-side (because
  // OData on Azure Table Storage can't express that disjunction), but pushing
  // a generous alert_time floor to the API avoids the unfiltered full-table
  // scan that was silently truncating responses on the All-tenants view.
  // The +24h buffer covers late-generated / backfilled alerts whose alert_time
  // trails their event_time. dateFrom='' (All preset) keeps hours=0 so the
  // server returns everything.
  const hoursForApi = useMemo(() => {
    if (!dateFrom) return 0;
    const fromMs = new Date(`${dateFrom}T00:00:00`).getTime();
    if (!Number.isFinite(fromMs)) return 0;
    const hours = Math.ceil((Date.now() - fromMs) / 3_600_000);
    return Math.max(1, hours) + 24;
  }, [dateFrom]);

  // Load alerts when tenant, server-side filters, or the date range change.
  // The dateTo upper bound + OR(alertTime, eventTime) disjunction are still
  // applied client-side in filteredAlerts.
  const loadAlerts = useCallback(() => {
    setAlertsLoading(true);
    setAlertsError(null);
    // Deep-link default: fetch ONLY the linked alert via the API point-read
    // fast path — avoids scanning the tenant's entire alert history just to
    // expand one row. "Load all alerts" flips loadFullList to do the normal load.
    const deepSingle = deepLinkActive && !loadFullList;
    const tenantForApi = deepSingle ? deepTenantId : selectedTenant;
    const opts = deepSingle
      ? { alertId: deepAlertId, hours: 0 }
      : {
          status: (statusFilter && statusFilter !== 'test') ? statusFilter : undefined,
          severity: severityFilter || undefined,
          hours: hoursForApi,
        };
    fetchAlerts(tenantForApi, opts)
      .then((data) => { setAlerts(data); setPage(1); })
      .catch((err) => setAlertsError(err.message))
      .finally(() => setAlertsLoading(false));
  }, [selectedTenant, statusFilter, severityFilter, hoursForApi, deepLinkActive, loadFullList, deepAlertId, deepTenantId]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // Load test results when checkbox is checked.
  // Always fetch without an hours filter so the user sees all test results
  // they opted into.  Client-side 24h filtering is applied in the
  // normalizedTestResults memo below using alertTime (when the test was run).
  const loadTestResults = useCallback(() => {
    if (!showTestAlerts) { setTestResults([]); return; }
    setTestLoading(true);
    fetchTestResults({ hours: 0 })
      .then((data) => setTestResults(data))
      .catch(() => setTestResults([]))
      .finally(() => setTestLoading(false));
  }, [showTestAlerts]);

  useEffect(() => { loadTestResults(); }, [loadTestResults]);

  // Refresh button handler — reloads both live alerts and (if visible) test
  // results, so a click refreshes everything shown on the page.
  const refreshAll = useCallback(() => {
    loadAlerts();
    loadTestResults();
  }, [loadAlerts, loadTestResults]);

  // Normalize test results to match alert shape for unified display.
  // 24h filtering is handled in filteredAlerts (same OR logic as regular alerts).
  const normalizedTestResults = useMemo(() => {
    return testResults.map((tr) => ({
      ...tr,
      alertId: tr.testResultId,
      alertTime: tr.alertTime || tr.eventTime,
      status: 'test',
      ipRiskLevel: tr.ipRiskLevel ?? null,
      ipDetections: tr.ipDetections || '',
      ipCountry: tr.ipCountry || '',
      ipProvider: tr.ipProvider || '',
      ipEnrichmentJson: tr.ipEnrichmentJson || '',
      baselineDeviation: tr.baselineDeviation || null,
      baselineContextJson: tr.baselineContextJson || '',
      baselineComparisonJson: tr.baselineComparisonJson || '',
      correlatedSignInJson: null,
      deviceIsCompliant: '',
      deviceName: '',
      deviceOs: '',
      _isTestResult: true,
    }));
  }, [testResults]);

  // Distinct rule names and users from loaded alerts + test results (for column filter dropdowns)
  const allItems = useMemo(() => [...alerts, ...normalizedTestResults], [alerts, normalizedTestResults]);
  const ruleOptions = useMemo(() => [...new Set(allItems.map((a) => a.ruleName).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [allItems]);
  const userOptions = useMemo(() => [...new Set(allItems.map((a) => a.userId).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [allItems]);

  // Confirmed-compromise set for the collapsed-row indicator (handoff §4): surface
  // the credential verdict on the un-expanded row so analysts triage it first.
  // Computed once per data load. Parsing rawEventJson is gated to pass-through
  // alerts that actually carry a `_signin_outcome` so large/All-tenant lists don't
  // parse every row; the persisted re-check field (signinOutcomeJson) is read
  // without parsing rawEventJson. Keyed by object identity — the same refs flow
  // through filter/sort into pageAlerts.
  const compromisedAlerts = useMemo(() => {
    const set = new Set();
    for (const a of allItems) {
      let rawEv = null;
      if (a.logType === 'security_alert' && typeof a.rawEventJson === 'string'
          && !a.rawEventJson.startsWith('blob:') && a.rawEventJson.includes('_signin_outcome')) {
        try { rawEv = JSON.parse(a.rawEventJson); } catch { rawEv = null; }
      }
      const outcome = effectiveSigninOutcome(a, rawEv);
      if (outcome && isCompromised(outcome)) set.add(a);
    }
    return set;
  }, [allItems]);

  // Active date-range preset, derived from current dateFrom/dateTo. Returns
  // '24h' | '3d' | '7d' | '30d' | 'All' when the range matches exactly, else null.
  // Computed (not sticky) so manual edits that drift off a preset clear the
  // highlight and tweaks back onto a preset re-apply it automatically.
  const activePreset = useMemo(() => {
    if (!dateFrom && !dateTo) return 'All';
    const today = todayYmd();
    if (dateTo !== today) return null;
    if (dateFrom === daysAgoYmd(1))  return '24h';
    if (dateFrom === daysAgoYmd(3))  return '3d';
    if (dateFrom === daysAgoYmd(7))  return '7d';
    if (dateFrom === daysAgoYmd(30)) return '30d';
    return null;
  }, [dateFrom, dateTo]);

  // Tenant name lookup for "All tenants" view (declared before filteredAlerts so
  // the search filter can match on tenant name without hitting the TDZ).
  const tenantNameMap = useMemo(() => {
    const map = {};
    for (const t of tenants) map[t.TenantID] = t.FriendlyName || t.Domain || t.TenantID;
    return map;
  }, [tenants]);

  // Client-side filters
  const filteredAlerts = useMemo(() => {
    // Merge test results when checkbox is checked
    let result = showTestAlerts
      ? [...alerts, ...normalizedTestResults]
      : [...alerts];

    // Tenant scope: live alerts are already tenant-scoped server-side via
    // fetchAlerts(selectedTenant, ...), but test results are fetched without a
    // tenant param (GetTestResults has no tenant filter), so merged-in test
    // rows leak across tenants. Re-apply the tenant filter client-side when a
    // specific tenant is selected — harmless to the already-scoped live alerts.
    // Some test-result rows ship an empty top-level tenantId, so recover it
    // from rawEventJson (mirrors AlertDetail's effectiveTenantId fallback)
    // before dropping a row.
    if (selectedTenant !== 'all') {
      const rowTenantId = (a) => {
        if (a.tenantId) return a.tenantId;
        if (a.rawEventJson && !String(a.rawEventJson).startsWith('blob:')) {
          try {
            const r = JSON.parse(a.rawEventJson);
            return r?.tenant_id || r?.tenantId || r?.resourceTenantId || r?.homeTenantId || '';
          } catch { /* fall through */ }
        }
        return '';
      };
      result = result.filter((a) => rowTenantId(a) === selectedTenant);
    }

    // Date range filter: show if alertTime (when generated) OR eventTime (when
    // event occurred) falls within [dateFrom 00:00:00, dateTo 23:59:59.999]
    // interpreted in local time. Empty string on either side = unbounded.
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;
    if (fromMs !== null || toMs !== null) {
      const inRange = (t) => {
        if (t == null) return false;
        if (fromMs !== null && t < fromMs) return false;
        if (toMs !== null && t > toMs) return false;
        return true;
      };
      result = result.filter((a) =>
        inRange(parseAlertTs(a.alertTime)) || inRange(parseAlertTs(a.eventTime))
      );
    }

    // Status: if explicitly filtered, apply client-side (needed for test results mixed in).
    // Only apply showClosed exclusion when no explicit status filter is set.
    if (statusFilter) {
      result = result.filter((a) => a.status === statusFilter);
    } else if (!showClosed) {
      result = result.filter((a) => a.status !== 'closed');
    }
    if (severityFilter) result = result.filter((a) => a.severity === severityFilter);
    if (ruleFilter) result = result.filter((a) => a.ruleName === ruleFilter);
    if (userFilter) result = result.filter((a) => a.userId === userFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((a) =>
        (a.userId || '').toLowerCase().includes(q) ||
        (a.clientIp || '').toLowerCase().includes(q) ||
        (a.ruleName || '').toLowerCase().includes(q) ||
        (a.ipCountry || '').toLowerCase().includes(q) ||
        (a.operation || '').toLowerCase().includes(q) ||
        (a.ipProvider || '').toLowerCase().includes(q) ||
        (tenantNameMap[a.tenantId] || '').toLowerCase().includes(q)
      );
    }

    // Sort by the active sort column (Alert Time or Event Time).
    // Empty/missing values sort to the end regardless of direction.
    result.sort((a, b) => {
      const aVal = a[sortColumn] || '';
      const bVal = b[sortColumn] || '';
      if (aVal === bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [alerts, normalizedTestResults, selectedTenant, dateFrom, dateTo, showClosed, showTestAlerts, statusFilter, severityFilter, ruleFilter, userFilter, search, sortColumn, sortDirection, tenantNameMap]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [filteredAlerts.length, dateFrom, dateTo, statusFilter, severityFilter, ruleFilter, userFilter, search, showClosed, showTestAlerts]);

  // Deep-link resolution: once alerts have loaded for the targeted tenant, jump
  // to the page holding the alert (declared after the reset-to-1 effect so it
  // wins on the same render), or flag it not-found for the inline banner.
  useEffect(() => {
    if (!deepLinkActive || alertsLoading || selectedTenant !== deepTenantId) return;
    const idx = filteredAlerts.findIndex((a) => (a.alertId || a.rowKey) === deepAlertId);
    if (idx < 0) { setDeepNotFound(true); return; }
    setDeepNotFound(false);
    if (!deepPageHandledRef.current) {
      setPage(Math.floor(idx / pageSize) + 1);
      deepPageHandledRef.current = true;
    }
  }, [deepLinkActive, deepAlertId, deepTenantId, selectedTenant, alertsLoading, filteredAlerts, pageSize]);

  // Paginate
  const totalPages = Math.max(1, Math.ceil(filteredAlerts.length / pageSize));
  const pageAlerts = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAlerts.slice(start, start + pageSize);
  }, [filteredAlerts, page, pageSize]);

  // Summary counts (across all loaded alerts, before client filters)
  const counts = useMemo(() => {
    const out = { total: alerts.length, new: 0, acknowledged: 0, closed: 0, critical: 0, high: 0, medium: 0, test: normalizedTestResults.length };
    for (const a of alerts) {
      if (a.status in out) out[a.status]++;
      if (a.severity === 'critical') out.critical++;
      else if (a.severity === 'high') out.high++;
      else if (a.severity === 'medium') out.medium++;
    }
    return out;
  }, [alerts, normalizedTestResults]);

  const showTenantColumn = selectedTenant === 'all';

  const handleStatusChange = useCallback((alertId, tenantId, status) => {
    // Closing requires a resolution note — defer to the modal, which calls
    // confirmClose once the analyst supplies a reason.
    if (status === 'closed') {
      const target = alerts.find((a) => a.alertId === alertId);
      // Open the modal immediately; resolve any linked live PSA ticket in the
      // background so the modal can offer to close it too.
      setPendingClose({ alertId, tenantId, ruleName: target?.ruleName, ticket: null, ticketLoading: !target?._isTestResult, checkedTicket: !target?._isTestResult, ticketingMapped: false, ticketingConfigured: false });
      if (!target?._isTestResult) {
        getAlertTicket(alertId, tenantId)
          .then((d) => setPendingClose((pc) => (
            pc && pc.alertId === alertId ? { ...pc, ticket: (d?.ticketLinked ? d : null), ticketingMapped: !!d?.ticketingMapped, ticketingConfigured: !!d?.ticketingConfigured, ticketLoading: false } : pc
          )))
          .catch(() => setPendingClose((pc) => (
            pc && pc.alertId === alertId ? { ...pc, ticketLoading: false } : pc
          )));
      }
      return;
    }
    (async () => {
      setUpdatingId(alertId);
      try {
        await updateAlertStatus(alertId, tenantId, status);
        // Reopening / acknowledging clears the prior resolution note (matches the API).
        const patch = status === 'new'
          ? { status, closeReason: '', closedBy: '', closedUtc: '', closeSource: '', psaTicketId: '', psaTicketNumber: '' }
          : { status };
        setAlerts((prev) => prev.map((a) => (a.alertId === alertId ? { ...a, ...patch } : a)));
      } catch (err) {
        console.error('Failed to update alert:', err);
      } finally {
        setUpdatingId(null);
      }
    })();
  }, [alerts]);

  // Re-check sign-in outcome resolved (SigninOutcomeBanner). The backend has already
  // persisted the outcome onto the DetectionAlerts row; mirror it onto the in-memory
  // alert so the resolved verdict survives collapsing + re-expanding the row (which
  // unmounts the banner) without a full refetch. Writes the same camelCase fields
  // GetAlerts surfaces, so effectiveSigninOutcome() + the compromised-row badge pick
  // it up. Stable identity (only setAlerts, which is stable) so the banner's callback
  // dep doesn't churn.
  const handleSigninOutcomeResolved = useCallback((alertId, res) => {
    if (!alertId || !res?.outcome) return;
    let outcomeJson;
    try { outcomeJson = JSON.stringify(res.outcome); } catch { return; }
    const patch = {
      signinOutcomeJson: outcomeJson,
      signinOutcomeStatus: res.outcome.status || '',
      signinOutcomeCheckedBy: res.checkedBy || '',
      signinOutcomeCheckedUtc: res.checkedUtc || '',
    };
    setAlerts((prev) => prev.map((a) => (a.alertId === alertId ? { ...a, ...patch } : a)));
  }, []);

  // Source audit event linked (LinkedAuditEventPanel). The backend already persisted
  // the resolved detail onto the DetectionAlerts row; mirror it onto the in-memory
  // alert so the link survives collapsing + re-expanding the row (which unmounts the
  // panel) without a refetch. Writes the same camelCase fields GetAlerts surfaces, so
  // effectiveLinkedAuditEvent() picks it up.
  const handleLinkedAuditResolved = useCallback((alertId, res) => {
    if (!alertId || !res?.linkedAuditEvent) return;
    let linkedJson;
    try { linkedJson = JSON.stringify(res.linkedAuditEvent); } catch { return; }
    const patch = {
      linkedAuditEventJson: linkedJson,
      linkedAuditEventStatus: res.linkConfidence || 'unique',
      linkedAuditCheckedBy: res.checkedBy || '',
      linkedAuditCheckedUtc: res.checkedUtc || '',
    };
    setAlerts((prev) => prev.map((a) => (a.alertId === alertId ? { ...a, ...patch } : a)));
  }, []);

  // Directory object name resolved via the "Resolve Name" button. The backend
  // already persisted it onto the DetectionAlerts row's resolved_directory_names_json
  // map; mirror it onto the in-memory alert so it survives collapse/re-expand (which
  // unmounts AlertDetail) without a refetch, merging into any existing map.
  const handleDirNameResolved = useCallback((alertId, id, info) => {
    if (!alertId || !id || !info?.name) return;
    setAlerts((prev) => prev.map((a) => {
      if (a.alertId !== alertId) return a;
      let map = {};
      if (a.resolvedDirectoryNamesJson) { try { map = JSON.parse(a.resolvedDirectoryNamesJson) || {}; } catch { map = {}; } }
      map[id] = { name: info.name, type: info.type };
      let json;
      try { json = JSON.stringify(map); } catch { return a; }
      return { ...a, resolvedDirectoryNamesJson: json };
    }));
  }, []);

  const confirmClose = useCallback(async (reason, alsoCloseTicket, timeMinutes = 0) => {
    if (!pendingClose) return;
    const { alertId, tenantId } = pendingClose;
    setCloseSubmitting(true);
    setUpdatingId(alertId);
    try {
      await updateAlertStatus(alertId, tenantId, 'closed', reason);
      const nowUtc = new Date().toISOString();
      setAlerts((prev) => prev.map((a) => (
        a.alertId === alertId
          ? { ...a, status: 'closed', closeReason: reason, closedBy: user?.upn || user?.userDetails || '', closedUtc: nowUtc, closeSource: 'analyst', psaTicketId: '', psaTicketNumber: '' }
          : a
      )));
      setPendingClose(null);

      // Push the resolution to the linked PSA ticket (best-effort). The local
      // close above stands regardless of the PSA outcome. When minutes were
      // entered, the API logs a time entry (attributed to the caller's mapped PSA
      // user) before closing.
      if (alsoCloseTicket) {
        try {
          const r = await closeAlertTicket(alertId, tenantId, reason, timeMinutes);
          const parts = [];
          if (r?.closed) parts.push(r.message || 'PSA ticket closed.');
          else if (r?.ticketLinked) parts.push(r.message || 'PSA ticket close incomplete.');
          if (timeMinutes > 0 && r?.timeMessage) parts.push(r.timeMessage);
          if (parts.length) {
            const ok = !!r?.closed && (timeMinutes <= 0 || !!r?.timeLogged);
            setTicketNotice({ ok, message: parts.join(' ') });
          }
        } catch (e) {
          setTicketNotice({ ok: false, message: `PSA ticket close failed: ${e.message || e}` });
        }
      }
    } catch (err) {
      console.error('Failed to close alert:', err);
    } finally {
      setCloseSubmitting(false);
      setUpdatingId(null);
    }
  }, [pendingClose, user]);

  // Auto-dismiss the PSA ticket-close toast.
  useEffect(() => {
    if (!ticketNotice) return;
    const t = setTimeout(() => setTicketNotice(null), 7000);
    return () => clearTimeout(t);
  }, [ticketNotice]);

  return (
    <div className="p-6 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BellIcon className="h-6 w-6 text-blue-400" />
          <div>
            <h1 className="text-lg font-semibold text-gray-100 flex items-center gap-1.5">
              Detection Alerts
              {/* Contextual jump into the User Guide section for this page. */}
              <Link
                to="/guide/alerts"
                title="Open the Alerts user guide"
                className="text-gray-600 hover:text-blue-400 transition-colors"
              >
                <QuestionMarkCircleIcon className="h-4 w-4" />
              </Link>
            </h1>
            <p className="text-xs text-gray-500">Automated rule-based detections from M365 audit logs</p>
          </div>
        </div>
        <RefreshButton
          loading={alertsLoading || testLoading}
          onClick={refreshAll}
          iconClassName="h-4 w-4"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 disabled:opacity-40 transition-colors"
        />
      </div>

      {/* Tenant selector */}
      <div className="mb-5">
        <label className="block text-xs font-medium text-gray-400 mb-1">Tenant</label>
        <select
          value={selectedTenant}
          onChange={(e) => setSelectedTenant(e.target.value)}
          disabled={tenantsLoading}
          className="input-field min-w-[220px] text-sm"
        >
          <option value="all">All tenants</option>
          {tenants
            .map((t) => {
              const shortName = t.ShortName;
              const friendlyName = t.FriendlyName;
              const label = shortName && friendlyName
                ? `(${shortName}) ${friendlyName}`
                : (shortName || friendlyName || t.Domain || t.TenantID);
              return { id: t.TenantID, label };
            })
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
            .map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>

      {/* Summary cards */}
      {!alertsLoading && alerts.length > 0 && (
        <div className={`grid gap-3 mb-5 ${showTestAlerts ? 'grid-cols-7' : 'grid-cols-6'}`}>
          {[
            { label: 'Total', value: counts.total, color: 'text-gray-300' },
            { label: 'New', value: counts.new, color: 'text-red-400' },
            { label: 'Acknowledged', value: counts.acknowledged, color: 'text-amber-400' },
            { label: 'Critical', value: counts.critical, color: 'text-red-400' },
            { label: 'High', value: counts.high, color: 'text-orange-400' },
            { label: 'Medium', value: counts.medium, color: 'text-yellow-400' },
            ...(showTestAlerts ? [{ label: 'Test', value: counts.test, color: 'text-amber-400' }] : []),
          ].map(({ label, value, color }) => (
            <div key={label} className="card py-3 px-4 text-center">
              <div className={`text-xl font-bold ${color}`}>{value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter bar — search + toggles only; column dropdowns live in the table header */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlassIcon className="absolute left-2.5 top-2 h-4 w-4 text-gray-500 pointer-events-none" />
          <input
            type="text"
            placeholder="Search user, IP, rule, country, tenant…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-8 text-sm w-full"
          />
        </div>

        {/* Date range — defaults to last 1 day; empty bounds = All */}
        <div className="flex items-center gap-1.5 text-sm text-gray-400">
          <span className="text-gray-500">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            max={dateTo || undefined}
            className="input-field py-1 px-2 text-xs w-[135px]"
            aria-label="Alerts filter from date"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            min={dateFrom || undefined}
            className="input-field py-1 px-2 text-xs w-[135px]"
            aria-label="Alerts filter to date"
          />
          <div className="flex items-center gap-1 ml-1">
            {[
              { label: '24h', days: 1 },
              { label: '3d',  days: 3 },
              { label: '7d',  days: 7 },
              { label: '30d', days: 30 },
            ].map(({ label, days }) => {
              const isActive = activePreset === label;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => { setDateFrom(daysAgoYmd(days)); setDateTo(todayYmd()); }}
                  className={`px-1.5 py-0.5 text-xs rounded border transition-colors ${
                    isActive
                      ? 'bg-blue-600/20 text-blue-400 border-blue-500/50'
                      : 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                  }`}
                  title={`Last ${label}`}
                  aria-pressed={isActive}
                >
                  {label}
                </button>
              );
            })}
            {(() => {
              const isActive = activePreset === 'All';
              return (
                <button
                  type="button"
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className={`px-1.5 py-0.5 text-xs rounded border transition-colors ${
                    isActive
                      ? 'bg-blue-600/20 text-blue-400 border-blue-500/50'
                      : 'border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                  }`}
                  title="Clear date range (show all)"
                  aria-pressed={isActive}
                >
                  All
                </button>
              );
            })()}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
            className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
          />
          Show closed
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showTestAlerts}
            onChange={(e) => setShowTestAlerts(e.target.checked)}
            className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
          />
          Show Test Alerts
        </label>

        {/* Timezone picker */}
        <select
          value={selectedTz}
          onChange={(e) => handleTzChange(e.target.value)}
          className="ml-auto bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded px-2 py-1.5 focus:ring-blue-500 focus:border-blue-500"
        >
          {TIMEZONE_OPTIONS.map(tz => (
            <option key={tz.id} value={tz.id}>{tz.label} ({getTzOffset(tz.iana)})</option>
          ))}
        </select>
      </div>

      {/* Refresh-in-progress banner (shown when reloading over existing data) */}
      <RefreshingBanner
        show={(alertsLoading || testLoading) && alerts.length > 0}
        message={testLoading && !alertsLoading ? 'Loading test alerts…' : 'Refreshing alerts…'}
      />

      {/* Error */}
      {alertsError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-900/30 border border-red-700/50 text-sm text-red-300">
          {alertsError}
        </div>
      )}

      {/* Deep-link single-alert view: only the linked alert was loaded (fast). */}
      {deepLinkActive && !loadFullList && !deepNotFound && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-blue-900/20 border border-blue-700/40 text-sm text-blue-200 flex items-center justify-between gap-3">
          <span>Showing the single alert linked from the ticket.</span>
          <button
            onClick={() => { setLoadFullList(true); setDateFrom(daysAgoYmd(30)); setDateTo(todayYmd()); }}
            className="px-2.5 py-1 rounded border border-blue-600/60 text-blue-200 hover:bg-blue-800/40 transition-colors flex-shrink-0"
          >
            Load all alerts for this tenant (last 30 days)
          </button>
        </div>
      )}

      {/* Deep-link: alert not found / not authorized for this tenant */}
      {deepLinkActive && deepNotFound && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-amber-900/20 border border-amber-700/50 text-sm text-amber-300 flex items-start justify-between gap-3">
          <span>
            That alert couldn't be found — it may have been reprocessed or removed, or the
            ticket link's alert id may not match a stored alert. Showing the alert list instead.
          </span>
          <button
            onClick={() => setDeepNotFound(false)}
            className="text-amber-400 hover:text-amber-200 flex-shrink-0"
            title="Dismiss"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Table — header + filters always visible so users can change filters */}
      <div className="card p-0">
        <div className="overflow-x-auto">
        <table ref={tableRef} className="text-sm" style={{ tableLayout: 'fixed', width: `${alertTableWidth}px` }}>
            <thead>
              {/* Column labels */}
              <tr className="bg-gray-900/60">
                <th style={{ width: `${ALERT_ICON_COL_W}px` }} className="pl-3 pr-1 pt-2.5 pb-1" />
                <th style={{ width: `${ALERT_ICON_COL_W}px` }} className="px-1 pt-2.5 pb-1" />
                <th
                  ref={(el) => { thRefs.current[0] = el; }}
                  style={{ width: `${colWidths[0]}px` }}
                  className="px-3 pt-2.5 pb-1 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-gray-200 relative"
                  onClick={() => handleSort('alertTime')}
                >
                  Alert Time
                  <SortIcon col="alertTime" />
                  <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, 0); }}>
                    <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                  </div>
                </th>
                <th
                  ref={(el) => { thRefs.current[1] = el; }}
                  style={{ width: `${colWidths[1]}px` }}
                  className="px-3 pt-2.5 pb-1 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-gray-200 relative"
                  onClick={() => handleSort('eventTime')}
                >
                  Event Time
                  <SortIcon col="eventTime" />
                  <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, 1); }}>
                    <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                  </div>
                </th>
                <th
                  ref={(el) => { thRefs.current[2] = el; }}
                  style={{ width: `${colWidths[2]}px` }}
                  className="px-3 pt-2.5 pb-1 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide relative"
                >
                  Severity
                  <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, 2); }}>
                    <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                  </div>
                </th>
                <th
                  ref={(el) => { thRefs.current[3] = el; }}
                  style={{ width: `${colWidths[3]}px` }}
                  className="px-3 pt-2.5 pb-1 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide relative"
                >
                  Rule
                  <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, 3); }}>
                    <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                  </div>
                </th>
                <th
                  ref={(el) => { thRefs.current[4] = el; }}
                  style={{ width: `${colWidths[4]}px` }}
                  className="px-3 pt-2.5 pb-1 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide relative"
                >
                  User
                  <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, 4); }}>
                    <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                  </div>
                </th>
                <th
                  ref={(el) => { thRefs.current[5] = el; }}
                  style={{ width: `${colWidths[5]}px` }}
                  className="px-3 pt-2.5 pb-1 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide relative"
                >
                  IP / Country
                  <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, 5); }}>
                    <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                  </div>
                </th>
                <th
                  ref={(el) => { thRefs.current[6] = el; }}
                  style={{ width: `${colWidths[6]}px` }}
                  className="px-3 pt-2.5 pb-1 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide relative"
                >
                  Status
                  <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, 6); }}>
                    <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                  </div>
                </th>
                <th
                  ref={(el) => { thRefs.current[7] = el; }}
                  style={{ width: `${colWidths[7]}px` }}
                  className="px-3 pt-2.5 pb-1 text-right text-xs font-semibold text-gray-400 uppercase tracking-wide"
                >
                  Actions
                </th>
              </tr>
              {/* Column filter dropdowns */}
              <tr className="bg-gray-900/60 border-b border-gray-800">
                <td style={{ width: `${ALERT_ICON_COL_W}px` }} className="px-3 pb-2" />
                <td style={{ width: `${ALERT_ICON_COL_W}px` }} className="px-3 pb-2" />
                <td style={{ width: `${colWidths[0]}px` }} className="px-3 pb-2" />
                <td style={{ width: `${colWidths[1]}px` }} className="px-3 pb-2" />
                <td style={{ width: `${colWidths[2]}px` }} className="px-3 pb-2">
                  <ColFilter
                    value={severityFilter}
                    onChange={setSeverityFilter}
                    options={SEVERITY_OPTIONS}
                    allLabel="All"
                  />
                </td>
                <td style={{ width: `${colWidths[3]}px` }} className="px-3 pb-2">
                  <ColFilter
                    value={ruleFilter}
                    onChange={setRuleFilter}
                    options={ruleOptions}
                    allLabel="All"
                  />
                </td>
                <td style={{ width: `${colWidths[4]}px` }} className="px-3 pb-2">
                  <ColFilter
                    value={userFilter}
                    onChange={setUserFilter}
                    options={userOptions}
                    allLabel="All"
                  />
                </td>
                <td style={{ width: `${colWidths[5]}px` }} className="px-3 pb-2" />
                <td style={{ width: `${colWidths[6]}px` }} className="px-3 pb-2">
                  <ColFilter
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={STATUS_OPTIONS}
                    allLabel="All"
                    labelFn={statusLabel}
                  />
                </td>
                <td style={{ width: `${colWidths[7]}px` }} className="px-3 pb-2" />
              </tr>
            </thead>
            <tbody>
              {alertsLoading ? (
                <tr>
                  <td colSpan={10} className="py-16 text-center text-gray-500 text-sm">
                    <div className="flex flex-col items-center gap-3">
                      <ArrowPathIcon className="h-6 w-6 animate-spin text-blue-500" />
                      Loading alerts…
                    </div>
                  </td>
                </tr>
              ) : filteredAlerts.length === 0 ? ( /* table-body empty/loading state stays inline (must be a <tr>) */
                <tr>
                  <td colSpan={10} className="py-16 text-center text-gray-500 text-sm">
                    {statusFilter === 'test' && !showTestAlerts
                      ? "You must enable 'Show Test Alerts'."
                      : alerts.length === 0 ? 'No alerts found.' : 'No alerts match the current filters.'}
                  </td>
                </tr>
              ) : (
                pageAlerts.map((alert) => (
                  <AlertRow
                    key={alert.alertId}
                    alert={alert}
                    compromised={compromisedAlerts.has(alert)}
                    onStatusChange={handleStatusChange}
                    onSigninOutcomeResolved={handleSigninOutcomeResolved}
                    onLinkedAuditResolved={handleLinkedAuditResolved}
                    onDirNameResolved={handleDirNameResolved}
                    updatingId={updatingId}
                    showTenant={showTenantColumn}
                    tenantName={tenantNameMap[alert.tenantId]}
                    onShowRaw={setRawAlert}
                    tzIana={tzObj.iana}
                    isAdmin={isAdmin}
                    onOpenAllowlistModal={setAllowlistModalAlert}
                    allowlistRuleIds={allowlistRuleIds}
                    onOpenUserAppModal={setUserAppPairModalAlert}
                    userAppPairRuleIds={userAppPairRuleIds}
                    onOpenGroupAppModal={setGroupAppModalAlert}
                    groupAppRuleIds={groupAppRuleIds}
                    onOpenSuppressTitleModal={(a, meta) => setSuppressTitleModal({ alert: a, meta })}
                    onOpenSuppressDescModal={(a, meta, prefill) => setSuppressDescModal({ alert: a, meta, prefill })}
                    onOpenSuppressActorModal={(a, meta, prefill) => setSuppressActorModal({ alert: a, meta, prefill })}
                    passThroughRuleMeta={passThroughRules[alert.ruleId]}
                    onOpenBenignIpModal={setBenignIpModalAlert}
                    passthroughIpRuleIds={passthroughIpRuleIds}
                    forceExpandId={deepLinkActive ? deepAlertId : null}
                  />
                ))
              )}
            </tbody>
          </table>
          </div>
          {!alertsLoading && filteredAlerts.length > 0 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredAlerts.length}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            />
          )}
        </div>

      {rawAlert && <RawAlertModal alert={rawAlert} onClose={() => setRawAlert(null)} />}
      {pendingClose && (
        <CloseReasonModal
          alert={pendingClose}
          submitting={closeSubmitting}
          onCancel={() => { if (!closeSubmitting) setPendingClose(null); }}
          onConfirm={confirmClose}
        />
      )}
      {allowlistModalAlert && (
        <MailboxPairAllowlistFromAlertModal
          alert={allowlistModalAlert}
          onClose={() => setAllowlistModalAlert(null)}
          onApplied={() => { /* leave the modal open so the analyst can read the summary; close button dismisses */ }}
        />
      )}
      {userAppPairModalAlert && (
        <UserAppPairAllowlistFromAlertModal
          alert={userAppPairModalAlert}
          onClose={() => setUserAppPairModalAlert(null)}
        />
      )}
      {groupAppModalAlert && (
        <GroupAppAllowlistFromAlertModal
          alert={groupAppModalAlert}
          onClose={() => setGroupAppModalAlert(null)}
        />
      )}
      {suppressTitleModal && (
        <SuppressAlertTitleModal
          alert={suppressTitleModal.alert}
          ruleMeta={suppressTitleModal.meta}
          onClose={() => setSuppressTitleModal(null)}
        />
      )}
      {suppressDescModal && (
        <SuppressAlertDescriptionModal
          alert={suppressDescModal.alert}
          ruleMeta={suppressDescModal.meta}
          prefill={suppressDescModal.prefill}
          onClose={() => setSuppressDescModal(null)}
        />
      )}
      {suppressActorModal && (
        <SuppressAlertActorModal
          alert={suppressActorModal.alert}
          ruleMeta={suppressActorModal.meta}
          prefill={suppressActorModal.prefill}
          onClose={() => setSuppressActorModal(null)}
        />
      )}
      {benignIpModalAlert && (
        <BenignIpAllowlistFromAlertModal
          alert={benignIpModalAlert}
          onClose={() => setBenignIpModalAlert(null)}
        />
      )}
      {ticketNotice && (
        <div
          className={`fixed bottom-4 right-4 z-50 px-4 py-2.5 rounded-lg shadow-2xl text-sm max-w-md cursor-pointer ${
            ticketNotice.ok ? 'bg-emerald-700/95 text-emerald-50' : 'bg-amber-700/95 text-amber-50'
          }`}
          onClick={() => setTicketNotice(null)}
          title="Dismiss"
        >
          {ticketNotice.message}
        </div>
      )}
    </div>
  );
}
